import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  closeCopyReplace, 
  setTargetDocument, 
  setPreviewMode, 
  setComment,
  setOriginalData,
  setNewData,
  setLoading
} from '../../../redux/previewSlice';
import { getRevAllNodes, getRevisionsForRestore } from '../../../redux/procedureRevisionSlice';
import { copyProcedure, copyNodeVersionHistory, copyVersionProcedureTreeNode, getAllSectionsForPart } from '../../../redux/sectionSlice';
import { getAuditTrailByPreview, getAuditTrailByid } from '../../../redux/auditTrailSlice';
import { getAuditTrailData, extractVersionStructure } from '../../../util/getAuditTrailData';
import { showSnackbar } from '../../../redux/snackbarSlice';
import { generateSHA256 } from '../../../util/generateSHA256';
import { getRevAndTraining, prepareCurrentSnapshot, uploadJsonToS3, handleRedirectToCopiedNode } from '../../../util/AppUtils';
import SelectRevisionTable from '../assets/components/SelectRevision/SelectRevisionTable';
import VersionHistoryCopyAppBar from '../../../common/VersionHistoryCopyAppBar';
import CopyReplacePreview from './components/CopyReplacePreview';
import CopyReplaceConfirm from './components/CopyReplaceConfirm';
import PastePositionSelector from './components/PastePositionSelector';
import Loader from '../../../common/Loader';
import { generateNodeIndexing } from '../../../hooks/useNodeIndexing';
import { buildNodeIndexingForSectionCopy, prepareCopyNewContent } from '../Move/utils/sectionMoveCopyUtils';
import { buildNodeIndexingForStepCopy, prepareStepMoveNewContent } from '../Move/utils/stepMoveCopyUtils';
import { buildNodeIndexingForSubstepCopy, prepareSubstepMoveNewContent } from '../Move/utils/substepMoveCopyUtils';
import { getChildNodeDisplayNumber, formatChildTypeLabel } from '../Move/utils/childNodeMoveCopyUtils';
import {
  mergeAuditTrailRowVersionContentDeep,
  mergeAuditVersionContent,
} from '../../AuditTrail/utils/auditTrailProcedureIdentity';
import { omitVersionHistoryTreeCopyFromFields } from './versionHistoryProcedureTreeCopyPayload';
import { resolveProcedureNodeDataForVersionHistoryCopy } from './versionHistoryProcedureNodeData';
import {
  resolveSnapshot,
  getSnapshotParseOptionsForAuditRow,
} from '../../AuditTrail/ActionComponents/utils/fetchSnapshotContent';
import { BASE } from '../../../config';
import { uploadToS3 } from '../../Canvas/utils/imageUploadUtils';

const CHILD_NODE_ID_FIELDS = [
  'attachment_tree_node_id',
  'video_tree_node_id',
  'rich_text_node_id',
  'canvas_tree_node_id',
  'form_tree_node_id',
];

/** Same optional top-level fields as `createCanvas` in stepSlice (when present on source detail). */
/** Omit `canvas_json_data_file` when sending full `pages` (matches typical create body). */
const CANVAS_CREATE_OPTIONAL_KEYS = [
  'layout_config',
  'location_markers',
  'text_box_properties',
  'text_box_content_data',
  'shape_data',
  'image_data',
];

/** `version-node-copy/create` expects `rich-text`; tree/detail may use `rich_text` or `richText`. */
function normalizeVersionNodeCopyCreateType(raw) {
  if (raw === undefined || raw === null) return raw;
  const s = String(raw).trim();
  if (s === 'rich_text' || s === 'richText' || s === 'rich-text') return 'rich-text';
  if (s === 'canva' || s === 'canvas') return 'canvas';
  return raw;
}

function resolveCanvasJsonFileUrl(raw) {
  if (!raw || typeof raw !== 'string') return raw;
  const u = raw.trim();
  if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u;
  const base = (BASE || '').replace(/\/?$/, '');
  if (u.startsWith('/')) {
    return base ? `${base}${u}` : `${window.location.origin}${u}`;
  }
  return base ? `${base}/${u}` : u;
}

function parseCanvasJsonPayloadToPages(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results?.pages)) return data.results.pages;
  if (Array.isArray(data?.pages)) return data.pages;
  if (Array.isArray(data?.data?.pages)) return data.data.pages;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

/**
 * Create-canvas expects `pages` in the body; procedure-tree `detail` usually only has
 * `canvas_json_data_file` pointing at the JSON.
 */
async function resolveCanvasPagesForVersionCopy(canvasNode) {
  const det =
    canvasNode?.detail && typeof canvasNode.detail === 'object'
      ? canvasNode.detail
      : {};
  if (Array.isArray(det.pages) && det.pages.length > 0) {
    return JSON.parse(JSON.stringify(det.pages));
  }
  const url = det.canvas_json_data_file;
  if (!url || typeof url !== 'string') return [];
  const fetchUrl = resolveCanvasJsonFileUrl(url);
  const loadJson = async () => {
    try {
      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-cache',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    } catch {
      const { data } = await axios.get(fetchUrl, {
        responseType: 'json',
        timeout: 120000,
        withCredentials: true,
      });
      return data;
    }
  };
  try {
    const data = await loadJson();
    const pages = parseCanvasJsonPayloadToPages(data);
    return pages.length > 0 ? JSON.parse(JSON.stringify(pages)) : [];
  } catch (e) {
    console.warn('[VersionHistoryCopy] resolveCanvasPagesForVersionCopy:', e);
    return [];
  }
}

/** Same as CanvasForm save: backend persists canvas from S3 JSON, not only inline `pages`. */
async function uploadCanvasPagesJsonForVersionCopy(pages) {
  const pagesJsonString = JSON.stringify(pages, null, 2);
  const base64Json = btoa(unescape(encodeURIComponent(pagesJsonString)));
  const dataUrl = `data:application/json;base64,${base64Json}`;
  const fileName = `canvas-pages-${Date.now()}.json`;
  return uploadToS3(dataUrl, fileName, 'application/json');
}

function pickNewContentSnapshotUrl(auditRow) {
  if (!auditRow || typeof auditRow !== 'object') return null;
  const top = auditRow.new_content_snapshot;
  if (typeof top === 'string' && top.trim()) return top.trim();
  const vc =
    mergeAuditTrailRowVersionContentDeep(auditRow) ||
    mergeAuditVersionContent(auditRow) ||
    auditRow.version_content;
  const nested = vc?.new_content_snapshot;
  if (typeof nested === 'string' && nested.trim()) return nested.trim();
  return null;
}

/** Inline section envelope from audit row (nested version_content merged). */
function pickVersionHistoryInlineTreeEnvelope(auditRow) {
  if (!auditRow || typeof auditRow !== 'object') return [];
  const vc =
    mergeAuditTrailRowVersionContentDeep(auditRow) ||
    mergeAuditVersionContent(auditRow) ||
    auditRow.version_content ||
    {};
  const raw =
    vc.newContent ?? vc.originingalContent ?? vc.originalContent ?? [];
  return Array.isArray(raw) ? raw : [];
}

/** CREATE/UPDATE leaf audits: tree is on S3 new_content_snapshot, not inline newContent. */
async function loadProcedureTreeFromAuditNewSnapshot(auditRow) {
  if (!auditRow || typeof auditRow !== 'object') return null;
  const url = pickNewContentSnapshotUrl(auditRow);
  if (!url || typeof url !== 'string') return null;
  try {
    const opts = getSnapshotParseOptionsForAuditRow(auditRow);
    const { rows } = await resolveSnapshot(url, 'new', opts);
    if (Array.isArray(rows) && rows.length > 0) {
      return rows;
    }
  } catch (e) {
    console.warn(
      '[VersionHistoryCopy] Failed to parse audit new_content_snapshot:',
      e,
    );
  }
  return null;
}

/** Normalize `version/get-rev-version` (and similar) responses to a section array like getRevAllNodes. */
function extractSectionsFromGetRevVersionResponse(payload) {
  if (payload == null) return null;
  const looksLikeSectionRow = (row) =>
    row &&
    typeof row === 'object' &&
    (row.type === 'section' ||
      Array.isArray(row.steps) ||
      row.section_tree_node_id != null ||
      (row.tree_node_id != null &&
        (row.detail != null || row.version_details != null)));
  const tryArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const first = arr[0];
    if (first && looksLikeSectionRow(first)) return arr;
    if (
      arr.length >= 1 &&
      arr.every((x) => x && typeof x === 'object') &&
      arr.some((x) => looksLikeSectionRow(x))
    ) {
      return arr;
    }
    return null;
  };
  const direct = tryArray(payload.results) || tryArray(payload.data);
  if (direct) return direct;
  const obj = payload.results && typeof payload.results === 'object' && !Array.isArray(payload.results)
    ? payload.results
    : payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
      ? payload.data
      : null;
  if (obj) {
    const nested = [
      obj.sections,
      obj.procedure_tree,
      obj.all_nodes,
      obj.allNodesData,
      obj.nodes,
      obj.data,
    ];
    for (const n of nested) {
      const found = tryArray(n);
      if (found) return found;
    }
  }
  return null;
}

async function loadProcedureTreeFromAuditVersionId(versionId, dispatch) {
  if (versionId == null || String(versionId).trim() === '') return null;
  try {
    const resp = await dispatch(
      getRevisionsForRestore({ versionId: String(versionId).trim() }),
    ).unwrap();
    return extractSectionsFromGetRevVersionResponse(resp);
  } catch (e) {
    console.warn('[VersionHistoryCopy] getRevisionsForRestore failed:', e);
    return null;
  }
}

/**
 * Audit-trail copy source: frozen tree for that version row, then S3 snapshot, then live nodes (wrong era).
 */
async function resolveVersionHistorySourceSections({
  auditRow,
  versionId,
  sourcePartId,
  sourceRevisionId,
  dispatch,
}) {
  const vid =
    versionId != null && String(versionId).trim() !== ''
      ? String(versionId).trim()
      : auditRow?.version_id != null
        ? String(auditRow.version_id).trim()
        : '';

  const fromSnapshot = await loadProcedureTreeFromAuditNewSnapshot(auditRow);
  if (Array.isArray(fromSnapshot) && fromSnapshot.length > 0) {
    return fromSnapshot;
  }

  if (vid) {
    const fromVersion = await loadProcedureTreeFromAuditVersionId(vid, dispatch);
    if (Array.isArray(fromVersion) && fromVersion.length > 0) {
      return fromVersion;
    }
  }

  console.warn(
    '[VersionHistoryCopy] No snapshot/version tree; falling back to get-procedure-nodes.',
  );
  return await dispatch(
    getRevAllNodes({
      partId: sourcePartId,
      revisionId: sourceRevisionId,
      ...(vid ? { otherParams: `version_id=${encodeURIComponent(vid)}` } : {}),
    }),
  ).unwrap();
}

/**
 * Same source tree as revision select / paste preview: prefer cached VH sections, else
 * snapshot → get-rev-version → get-procedure-nodes, then optional node/node-data.
 * `handleConfirmSave` must use this instead of bare `getRevAllNodes` or save payloads
 * (e.g. rich_text_content) drift from View New / View Original.
 */
async function loadVersionHistorySourceTreeForConfirm({
  sourceDataCache,
  auditRow,
  versionId,
  sourcePartId,
  sourceRevisionId,
  auditTrailId,
  dispatch,
}) {
  if (Array.isArray(sourceDataCache) && sourceDataCache.length > 0) {
    return sourceDataCache;
  }
  if (!auditRow) return null;

  const versionContent =
    mergeAuditTrailRowVersionContentDeep(auditRow) ||
    mergeAuditVersionContent(auditRow) ||
    auditRow.version_content ||
    {};
  const rawData = pickVersionHistoryInlineTreeEnvelope(auditRow);

  let sourceResponse = await resolveVersionHistorySourceSections({
    auditRow,
    versionId,
    sourcePartId,
    sourceRevisionId,
    dispatch,
  });

  if (!sourceResponse || sourceResponse.length === 0) {
    if (rawData.length > 0) {
      const effectiveVid = String(versionId || auditRow.version_id || '').trim();
      const payload = {
        move_copy_version_id_ref: versionContent.move_copy_version_id_ref || '',
        created_at: auditRow.version_created_at || '',
        data: rawData,
        ...(effectiveVid ? { version_id: effectiveVid } : {}),
        ...(auditTrailId ? { audit_trail_id: auditTrailId } : {}),
      };
      try {
        const contentResponse = await dispatch(
          getAuditTrailByPreview(payload),
        ).unwrap();
        sourceResponse = extractSectionsFromNodeDataPreviewResponse(
          contentResponse,
        );
      } catch (e) {
        console.warn(
          '[VersionHistoryCopy] node/node-data fallback on confirm failed:',
          e,
        );
      }
    }
  }

  if (Array.isArray(sourceResponse) && sourceResponse.length > 0) {
    return sourceResponse;
  }
  return null;
}

/** Unwrap `node/node-data` preview response to section rows. */
function extractSectionsFromNodeDataPreviewResponse(contentResponse) {
  if (contentResponse == null) return [];
  let processedContent = null;
  if (contentResponse.result != null) processedContent = contentResponse.result;
  else if (contentResponse.data?.result != null)
    processedContent = contentResponse.data.result;
  else if (contentResponse.data != null) processedContent = contentResponse.data;
  else if (contentResponse.results != null) processedContent = contentResponse.results;
  else if (Array.isArray(contentResponse)) processedContent = contentResponse;
  else processedContent = contentResponse;
  if (processedContent != null && !Array.isArray(processedContent) && processedContent.data != null) {
    processedContent = processedContent.data;
  }
  return Array.isArray(processedContent)
    ? processedContent
    : processedContent
      ? [processedContent]
      : [];
}

/**
 * Match audit section id to a row from get-procedure-nodes. Audit may send DB `section_id_ref`
 * while the API section row uses `tree_node_id` as the procedure id — check both + detail.*.
 */
const sectionMatchesAuditSectionId = (section, targetId) => {
  if (!section || !targetId) return false;
  const d = section.detail || {};
  return (
    section.tree_node_id === targetId ||
    section.section_id_ref === targetId ||
    section.section_tree_node_id === targetId ||
    d.section_tree_node_id === targetId ||
    d.section_id_ref === targetId
  );
};

/** Audit openCopyReplace may only populate one of nodeId / sectionId; VC may use tree_node_id for procedure tree. */
const getSectionSourceMatchId = (sourceNode) => {
  if (!sourceNode || sourceNode.nodeType !== 'section') return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.nodeId ||
    sourceNode.sectionId ||
    sourceNode.section_id_ref ||
    sourceNode.tree_node_id ||
    d.section_tree_node_id ||
    d.section_id_ref ||
    d.procedure_tree_ref ||
    null;
  return id || null;
};

const parseSectionOrdinalFromTitle = (title) => {
  if (!title || typeof title !== 'string') return null;
  const m = title.trim().match(/^section\s+(\d+)$/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
};

/** When audit omits all section ids (MovePreviewLayout / incomplete VC), match by "Section N" ↔ sibling_order or section_name. */
const findSectionByFallbackTitle = (sections, sourceNode) => {
  if (!Array.isArray(sections) || !sourceNode?.nodeTitle) return null;
  const title = String(sourceNode.nodeTitle).trim();
  const ord = parseSectionOrdinalFromTitle(title);
  if (ord != null) {
    const bySibling = sections.find((s) => {
      const so = parseInt(
        s.sibling_order ?? s.procedure_tree?.sibling_order ?? '0',
        10,
      );
      return so === ord;
    });
    if (bySibling) return bySibling;
  }
  return (
    sections.find((s) => {
      const d = s.detail || {};
      const name = (d.section_name || s.section_name || '').trim();
      return name && name === title;
    }) || null
  );
};

/** Parent section id for step copy — audit VC may omit sectionId but set procedure_tree_ref / detail.* */
const getStepSectionMatchId = (sourceNode) => {
  if (!sourceNode || sourceNode.nodeType !== 'step') return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.sectionId ||
    sourceNode.section_id_ref ||
    sourceNode.section_tree_node_id ||
    d.section_tree_node_id ||
    d.section_id_ref ||
    d.procedure_tree_ref ||
    null;
  return id || null;
};

/** Step procedure id for matching steps[] rows */
const getStepSourceMatchId = (sourceNode) => {
  if (!sourceNode || sourceNode.nodeType !== 'step') return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.nodeId ||
    sourceNode.stepId ||
    sourceNode.step_id_ref ||
    sourceNode.tree_node_id ||
    d.step_tree_node_id ||
    d.step_id_ref ||
    d.procedure_tree_ref ||
    null;
  return id || null;
};

const parseStepOrdinalFromTitle = (title) => {
  if (!title || typeof title !== 'string') return null;
  const m = title.trim().match(/^step\s+(\d+)$/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
};

const parseSubstepOrdinalFromTitle = (title) => {
  if (!title || typeof title !== 'string') return null;
  const m = title.trim().match(/^substep\s+(\d+)$/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
};

/** When section/step ids are missing, match "Step N" to step.sibling_order within sections. */
const findStepByFallbackInSections = (sections, sourceNode) => {
  if (!Array.isArray(sections) || !sourceNode?.nodeTitle) return null;
  const title = String(sourceNode.nodeTitle).trim();
  const ord = parseStepOrdinalFromTitle(title);
  if (ord == null) return null;
  for (const sec of sections) {
    const steps = sec.steps || [];
    const byOrd = steps.find((st) => {
      const so = parseInt(
        st.sibling_order ?? st.procedure_tree?.sibling_order ?? '0',
        10,
      );
      return so === ord;
    });
    if (byOrd) return { sourceSection: sec, sourceStep: byOrd };
  }
  return null;
};

const getSubstepSectionMatchId = (sourceNode) => {
  if (!sourceNode || sourceNode.nodeType !== 'substep') return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.sectionId ||
    sourceNode.section_id_ref ||
    d.section_tree_node_id ||
    d.section_id_ref ||
    d.procedure_tree_ref ||
    null;
  return id || null;
};

const getSubstepStepMatchId = (sourceNode) => {
  if (!sourceNode || sourceNode.nodeType !== 'substep') return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.stepId ||
    sourceNode.step_id_ref ||
    d.step_tree_node_id ||
    d.step_id_ref ||
    null;
  return id || null;
};

const getSubstepSourceMatchId = (sourceNode) => {
  if (!sourceNode || sourceNode.nodeType !== 'substep') return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.nodeId ||
    sourceNode.substepId ||
    d.sub_step_tree_node_id ||
    d.substep_tree_node_id ||
    null;
  return id || null;
};

/** When section/step/substep ids are missing, match "Substep N" to substep.sibling_order within sections/steps. */
const findSubstepByFallbackInSections = (sections, sourceNode) => {
  if (!Array.isArray(sections) || !sourceNode?.nodeTitle) return null;
  const title = String(sourceNode.nodeTitle).trim();
  const ord = parseSubstepOrdinalFromTitle(title);
  
  if (ord === null) return null;
  
  for (const sec of sections) {
    for (const step of sec.steps || []) {
      const substeps = step.substeps || [];
      const target = substeps.find(ss => {
        const so = parseInt(ss.sibling_order, 10);
        return !Number.isNaN(so) && so === ord;
      });
      if (target) {
        return {
          sourceSection: sec,
          sourceStep: step,
          sourceSubstep: target,
        };
      }
    }
  }
  return null;
};

const stepMatchesAuditStepId = (step, stepNodeId) => {
  if (!step || !stepNodeId) return false;
  const d = step.detail || {};
  return (
    step.tree_node_id === stepNodeId ||
    step.step_id_ref === stepNodeId ||
    step.step_tree_node_id === stepNodeId ||
    d.step_tree_node_id === stepNodeId ||
    d.step_id_ref === stepNodeId ||
    d.procedure_tree_ref === stepNodeId
  );
};

const substepMatchesAuditSubstepId = (ss, substepNodeId) => {
  if (!ss || !substepNodeId) return false;
  const d = ss.detail || {};
  return (
    ss.tree_node_id === substepNodeId ||
    ss.substep_id_ref === substepNodeId ||
    ss.sub_step_tree_node_id === substepNodeId ||
    ss.substep_tree_node_id === substepNodeId ||
    d.sub_step_tree_node_id === substepNodeId ||
    d.substep_tree_node_id === substepNodeId ||
    d.procedure_tree_ref === substepNodeId
  );
};

/** When section id from audit does not match any section row, locate parent section by step id. */
const findSectionAndStepByStepId = (sections, stepNodeId) => {
  if (!stepNodeId || !Array.isArray(sections)) return null;
  for (const sec of sections) {
    const step = (sec.steps || []).find((st) => stepMatchesAuditStepId(st, stepNodeId));
    if (step) return { sourceSection: sec, sourceStep: step };
  }
  return null;
};

/** True if string looks like an http(s) media URL (excludes labels like "Video URL"). */
const looksLikeHttpUrl = (s) =>
  typeof s === 'string' &&
  s.length > 0 &&
  /^https?:\/\//i.test(s.trim());

const looksLikeMediaPath = (s) => {
  if (typeof s !== 'string' || !s.trim()) return false;
  const t = s.trim();
  if (looksLikeHttpUrl(t)) return true;
  if (t.startsWith('data:')) return true;
  if (
    t.startsWith('/') &&
    /\.(mp4|webm|mov|m4v|ogg|jpe?g|png|gif|webp|pdf|zip|docx?|xlsx?)(\?|$)/i.test(
      t,
    )
  )
    return true;
  if (/\.(amazonaws\.com|cloudfront\.net)/i.test(t)) return true;
  return false;
};

const coerceVideoUrlString = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'object') {
    const inner =
      v.video ||
      v.video_http_url ||
      v.video_url ||
      v.url ||
      v.src ||
      v.href ||
      v.uri ||
      v.file ||
      v.path;
    if (typeof inner === 'string') return inner.trim();
  }
  return '';
};

/** VideoForm "Video Url" text field → `detail.video_url` only (not playback / upload URL). */
const pickVideoUrlFormFieldFromDetail = (det) => {
  if (!det || typeof det !== 'object') return '';
  const u = typeof det.video_url === 'string' ? det.video_url.trim() : '';
  if (!u) return '';
  if (/^video\s*$/i.test(u)) return '';
  return u;
};

/**
 * Playback / file URL only: `video`, `videos[].video`, `video_http_url` — excludes top-level
 * `video_url` so copy payload matches VideoForm (`video_url` vs `video`).
 */
const pickPlaybackVideoUrlFromDetail = (det, treeChild) => {
  const bucket = [];
  const pushFromDet = (d) => {
    if (!d || typeof d !== 'object') return;
    bucket.push(coerceVideoUrlString(d.video));
    bucket.push(coerceVideoUrlString(d.video_http_url));
    if (Array.isArray(d.videos)) {
      d.videos.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        bucket.push(coerceVideoUrlString(entry.video));
        bucket.push(coerceVideoUrlString(entry.video_http_url));
        bucket.push(coerceVideoUrlString(entry.url));
        bucket.push(coerceVideoUrlString(entry.src));
      });
    }
  };
  pushFromDet(det);
  if (treeChild && typeof treeChild === 'object') {
    bucket.push(coerceVideoUrlString(treeChild.video));
    bucket.push(coerceVideoUrlString(treeChild.video_http_url));
    if (Array.isArray(treeChild.videos)) {
      treeChild.videos.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        bucket.push(coerceVideoUrlString(entry.video));
        bucket.push(coerceVideoUrlString(entry.video_http_url));
      });
    }
  }
  for (const c of bucket) {
    if (c && looksLikeMediaPath(c)) return c;
  }
  for (const c of bucket) {
    if (c) return c;
  }
  return '';
};

/** Playable URL from video `detail` and optional tree child (nested `videos[]`, objects, etc.). */
const pickVideoStreamUrlFromDetail = (det, treeChild) => {
  const bucket = [];
  const pushFromDet = (d) => {
    if (!d || typeof d !== 'object') return;
    bucket.push(coerceVideoUrlString(d.video));
    bucket.push(coerceVideoUrlString(d.video_http_url));
    if (Array.isArray(d.videos)) {
      d.videos.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        bucket.push(coerceVideoUrlString(entry.video));
        bucket.push(coerceVideoUrlString(entry.video_http_url));
        bucket.push(coerceVideoUrlString(entry.video_url));
        bucket.push(coerceVideoUrlString(entry.url));
        bucket.push(coerceVideoUrlString(entry.src));
      });
    }
    bucket.push(coerceVideoUrlString(d.video_url));
  };
  pushFromDet(det);
  if (treeChild && typeof treeChild === 'object') {
    bucket.push(coerceVideoUrlString(treeChild.video));
    bucket.push(coerceVideoUrlString(treeChild.video_http_url));
    if (Array.isArray(treeChild.videos)) {
      treeChild.videos.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        bucket.push(coerceVideoUrlString(entry.video));
        bucket.push(coerceVideoUrlString(entry.video_http_url));
        bucket.push(coerceVideoUrlString(entry.video_url));
      });
    }
  }
  for (const c of bucket) {
    if (c && looksLikeMediaPath(c)) return c;
  }
  for (const c of bucket) {
    if (c) return c;
  }
  return '';
};

const deepFindFirstMediaUrl = (value, depth = 0, maxDepth = 8) => {
  if (depth > maxDepth || value == null) return '';
  if (typeof value === 'string') {
    const t = value.trim();
    return looksLikeMediaPath(t) ? t : '';
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const f = deepFindFirstMediaUrl(item, depth + 1, maxDepth);
      if (f) return f;
    }
    return '';
  }
  if (typeof value === 'object') {
    const preferredKeys = [
      'video',
      'video_http_url',
      'video_url',
      'attachment',
      'url',
      'src',
      'href',
      'file',
      'path',
      'media_url',
      'file_url',
      's3_url',
      'storage_path',
    ];
    for (const k of preferredKeys) {
      if (Object.prototype.hasOwnProperty.call(value, k)) {
        const f = deepFindFirstMediaUrl(value[k], depth + 1, maxDepth);
        if (f) return f;
      }
    }
    for (const k of Object.keys(value)) {
      if (preferredKeys.includes(k)) continue;
      const f = deepFindFirstMediaUrl(value[k], depth + 1, maxDepth);
      if (f) return f;
    }
  }
  return '';
};

/** Build `attachment: string[]` for version copy / create (AttachmentForm jsonData). */
const normalizeAttachmentUrlsForVersionCopy = (det, treeChild) => {
  const urls = [];
  const add = (u) => {
    if (u == null || u === '') return;
    const s = typeof u === 'string' ? u.trim() : String(u).trim();
    if (s && !urls.includes(s)) urls.push(s);
  };
  const consume = (d) => {
    if (!d || typeof d !== 'object') return;
    if (Array.isArray(d.attachment)) {
      d.attachment.forEach((item) => {
        if (typeof item === 'string') add(item);
        else if (item && typeof item === 'object' && item.attachment != null) {
          add(item.attachment);
        }
      });
    } else if (typeof d.attachment === 'string') {
      add(d.attachment);
    }
    if (Array.isArray(d.attachments)) {
      d.attachments.forEach((item) => {
        if (typeof item === 'string') add(item);
        else if (item && typeof item === 'object' && item.attachment != null) {
          add(String(item.attachment));
        }
      });
    }
  };
  consume(det);
  if (treeChild && typeof treeChild === 'object') {
    consume(treeChild.detail);
    if (Array.isArray(treeChild.attachment)) {
      treeChild.attachment.forEach((x) => {
        if (typeof x === 'string') add(x);
      });
    }
  }
  return urls;
};

const findSectionStepSubstepBySubstepId = (sections, substepNodeId) => {
  if (!substepNodeId || !Array.isArray(sections)) return null;
  for (const sec of sections) {
    for (const st of sec.steps || []) {
      const ss = (st.substeps || []).find((s) =>
        substepMatchesAuditSubstepId(s, substepNodeId),
      );
      if (ss) return { sourceSection: sec, sourceStep: st, sourceSubstep: ss };
    }
  }
  return null;
};

/** Resolve element id for attachment / video / rich-text / canvas / form (audit may use tree_node_id vs *_*_node_id). */
const getChildSourceMatchId = (sourceNode) => {
  if (!sourceNode) return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.nodeId ||
    sourceNode.tree_node_id ||
    sourceNode.preview_tree_node_id ||
    sourceNode.element_id ||
    d.rich_text_tree_node_id ||
    d.rich_text_node_id ||
    d.video_tree_node_id ||
    d.canvas_tree_node_id ||
    d.canva_tree_node_id ||
    d.attachment_tree_node_id ||
    d.form_tree_node_id ||
    d.preview_tree_node_id ||
    null;
  
  return id || null;
};

const getChildSectionMatchId = (sourceNode) => {
  if (!sourceNode) return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.sectionId ||
    sourceNode.section_id_ref ||
    sourceNode.section_tree_node_id ||
    d.section_tree_node_id ||
    d.section_id_ref ||
    d.procedure_tree_ref ||
    null;
  return id || null;
};

const getChildStepMatchId = (sourceNode) => {
  if (!sourceNode) return null;
  const d = sourceNode.detail || {};
  const id =
    sourceNode.stepId ||
    sourceNode.step_id_ref ||
    d.step_tree_node_id ||
    d.step_id_ref ||
    null;
  return id || null;
};

/** Match procedure-tree child to audit ids (tree + detail.* on the child row). */
const childMatchesSourceNode = (c, sourceNode) => {
  const id = getChildSourceMatchId(sourceNode);
  if (!c || !id) {
    return false;
  }
  if (c.tree_node_id === id) {
    return true;
  }
  const d = c.detail || {};
  const matches = (
    d.canvas_tree_node_id === id ||
    d.attachment_tree_node_id === id ||
    d.video_tree_node_id === id ||
    d.rich_text_node_id === id ||
    d.rich_text_tree_node_id === id ||
    d.form_tree_node_id === id ||
    d.procedure_tree_ref === id
  );
  
  return matches;
};

/**
 * Redux openCopyReplace often omits element/section/step ids; merged audit row has the same
 * fields as PreviewLayout handleCopy (nested version_content, parent merge).
 */
const enrichChildSourceNodeFromAuditRow = (sourceNode, auditRow, breadcrumbText) => {
  if (!sourceNode || !auditRow) {
    return sourceNode;
  }
  
  // Debug the full audit trail structure
  const merged =
    mergeAuditTrailRowVersionContentDeep(auditRow) ||
    mergeAuditVersionContent(auditRow) ||
    {};
  const raw = auditRow.version_content;
  const rawFlat =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? { ...raw, ...(raw.version_content && typeof raw.version_content === 'object' ? raw.version_content : {}) }
      : {};
  const vc = { ...rawFlat, ...merged };
  const pick = (existing, ...candidates) => {
    if (existing != null && existing !== '') return existing;
    for (const c of candidates) {
      if (c != null && c !== '') return c;
    }
    return existing;
  };
  const nodeId = pick(
    sourceNode.nodeId,
    sourceNode.tree_node_id,
    vc.video_tree_node_id,
    vc.canvas_tree_node_id,
    vc.canva_tree_node_id,
    vc.rich_text_node_id,
    vc.rich_text_tree_node_id,
    vc.attachment_tree_node_id,
    vc.form_tree_node_id,
    vc.tree_node_id,
    vc.preview_tree_node_id,
    vc.element_id,
  );
  const sectionId = pick(
    sourceNode.sectionId,
    sourceNode.section_tree_node_id,
    vc.section_tree_node_id,
    vc.section_id_ref,
    vc.procedure_tree_ref,
  );
  const stepId = pick(
    sourceNode.stepId,
    vc.step_id_ref,
    vc.step_tree_node_id,
  );
  const substepId = pick(
    sourceNode.substepId,
    vc.substep_id_ref,
    vc.sub_step_tree_node_id,
    vc.substep_tree_node_id,
  );
  const nodeTitle = pick(
    sourceNode.nodeTitle,
    vc.nodeTitle,
    vc.pageTitle,
    auditRow.action_to_show,
  );
  if (
    nodeId === sourceNode.nodeId &&
    sectionId === sourceNode.sectionId &&
    stepId === sourceNode.stepId &&
    substepId === sourceNode.substepId &&
    nodeTitle === sourceNode.nodeTitle
  ) {
    return sourceNode;
  }
  const next = { ...sourceNode, nodeId, sectionId, stepId, substepId };
  if (nodeTitle != null && nodeTitle !== '') next.nodeTitle = nodeTitle;
  
  // Extract breadcrumb information from breadcrumbText prop for video nodes
  if (sourceNode.nodeType === 'video') {
    // Extract tree_node_id from audit trail data for video nodes
    if (!sourceNode.nodeId && !sourceNode.tree_node_id) {
      // Try to find the video node in the audit trail version_content
      const candidates = collectVideoCandidateRowsFromAuditVc(vc);
      for (const candidate of candidates) {
        if (rowLooksLikeVideoNode(candidate)) {
          if (candidate.tree_node_id) {
            next.nodeId = candidate.tree_node_id;
            next.tree_node_id = candidate.tree_node_id;
            break;
          }
        }
      }
      
      // If still no ID, try to extract from detail.video_tree_node_id
      if (!next.nodeId && vc.detail?.video_tree_node_id) {
        next.nodeId = vc.detail.video_tree_node_id;
        next.tree_node_id = vc.detail.video_tree_node_id;
      }
      
      // If still no ID, try to extract from breadcrumb using global window data
      if (!next.nodeId && breadcrumbText && window.lastExtractedBreadcrumbData) {
        const breadcrumbData = window.lastExtractedBreadcrumbData;
        if (breadcrumbData.videoNode && breadcrumbData.videoNode.tree_node_id) {
          next.nodeId = breadcrumbData.videoNode.tree_node_id;
          next.tree_node_id = breadcrumbData.videoNode.tree_node_id;
        }
      }
      
      // If still no ID, try to extract video_tree_node_id from vc.detail
      if (!next.nodeId && vc.detail?.video_tree_node_id) {
        next.nodeId = vc.detail.video_tree_node_id;
        next.tree_node_id = vc.detail.video_tree_node_id;
      }
      
      // If still no ID, try to extract from videos array
      if (!next.nodeId && vc.videos && Array.isArray(vc.videos)) {
        for (const video of vc.videos) {
          if (video.video_tree_node_ref) {
            next.nodeId = video.video_tree_node_ref;
            next.tree_node_id = video.video_tree_node_ref;
            break;
          }
        }
      }
    }
    
    // Use breadcrumbText from props first, then fallback to extracting from vc.short_description
    let effectiveBreadcrumbText = breadcrumbText;
    
    // If no breadcrumbText from props, try to extract it from vc.short_description HTML content
    if (!effectiveBreadcrumbText && vc.short_description) {
      const htmlMatch = vc.short_description.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
      if (htmlMatch) {
        effectiveBreadcrumbText = htmlMatch[0].replace(/&gt;/g, '>');
      }
    }
    
    // If still no breadcrumb, try to extract from auditRow.short_description
    if (!effectiveBreadcrumbText && auditRow.short_description) {
      const auditHtmlMatch = auditRow.short_description.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
      if (auditHtmlMatch) {
        effectiveBreadcrumbText = auditHtmlMatch[0].replace(/&gt;/g, '>');
      }
    }
    
    if (!effectiveBreadcrumbText) {
      return next;
    }
    
    // Extract section number
    const sectionMatch = /section\s+(\d+)/i.exec(effectiveBreadcrumbText);
    if (sectionMatch) {
      next.globalSectionNumber = parseInt(sectionMatch[1], 10);
    }
    
    // Extract step number
    const stepMatch = /step\s+(\d+)/i.exec(effectiveBreadcrumbText);
    if (stepMatch) {
      next.globalStepNumber = parseInt(stepMatch[1], 10);
    }
    
    // Extract substep information
    const parts = effectiveBreadcrumbText.split(' > ');
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const substepMatch = /substep\s+(\d+)([A-Z])/i.exec(part);
      if (substepMatch) {
        const stepNum = parseInt(substepMatch[1], 10);
        const substepLetter = substepMatch[2];
        // Find the section before this substep
        let sectionNum = null;
        for (let j = i - 1; j >= 0; j--) {
          const sectionMatch = /section\s+(\d+)/i.exec(parts[j]);
          if (sectionMatch) {
            sectionNum = parseInt(sectionMatch[1], 10);
            break;
          }
        }
        
        next.globalSectionNumber = sectionNum;
        next.globalStepNumber = stepNum;
        next.isSubstepChild = true;
        next.substepIndex = substepLetter.charCodeAt(0) - 65; // A=0, B=1, etc.
        break;
      }
    }
    
  }
  
  return next;
};

/** Collect rows from audit version_content that may describe a single procedure node (video copy). */
const collectVideoCandidateRowsFromAuditVc = (vc) => {
  const out = [];
  if (!vc || typeof vc !== 'object') return out;
  if (Array.isArray(vc.procedure_node_data)) {
    vc.procedure_node_data.forEach((r) => {
      if (r && typeof r === 'object') out.push(r);
    });
  }
  const nc = vc.newContent;
  if (nc && typeof nc === 'object' && Array.isArray(nc.procedure_node_data)) {
    nc.procedure_node_data.forEach((r) => {
      if (r && typeof r === 'object') out.push(r);
    });
  }
  out.push(vc);
  return out;
};

const videoAuditRowMatchesChildId = (row, matchId) => {
  if (!row || !matchId) return false;
  const d = row.detail && typeof row.detail === 'object' ? row.detail : {};
  const ids = [
    row.tree_node_id,
    row.element_id,
    row.video_tree_node_id,
    d.video_tree_node_id,
    d.procedure_tree_ref,
  ].filter(Boolean);
  return ids.some((id) => id === matchId);
};

const rowLooksLikeVideoNode = (row) => {
  if (!row || typeof row !== 'object') return false;
  const t = String(row.type || '').toLowerCase();
  if (t === 'video') return true;
  const d = row.detail && typeof row.detail === 'object' ? row.detail : {};
  return !!(
    row.video ||
    row.video_url ||
    row.video_http_url ||
    d.video ||
    d.video_url ||
    d.video_http_url
  );
};

/**
 * For version-history copy: `video_url` (form field) vs `video` (playback) from audit rows
 * that match this node — same split as VideoForm create payload.
 */
const pickVideoFieldsFromAuditRowForNode = (auditRow, childSourceNode) => {
  if (!auditRow || !childSourceNode) {
    return { formVideoUrl: '', playbackUrl: '' };
  }
  const matchId = getChildSourceMatchId(childSourceNode);
  if (!matchId) return { formVideoUrl: '', playbackUrl: '' };
  const vc =
    mergeAuditTrailRowVersionContentDeep(auditRow) ||
    mergeAuditVersionContent(auditRow) ||
    (auditRow.version_content && typeof auditRow.version_content === 'object'
      ? auditRow.version_content
      : null) ||
    {};
  const candidates = collectVideoCandidateRowsFromAuditVc(vc);
  for (const row of candidates) {
    if (!rowLooksLikeVideoNode(row)) continue;
    if (!videoAuditRowMatchesChildId(row, matchId)) continue;
    const det = row.detail && typeof row.detail === 'object' ? row.detail : {};
    const formVideoUrl =
      pickVideoUrlFormFieldFromDetail(det) || pickVideoUrlFormFieldFromDetail(row);
    const playbackUrl =
      pickPlaybackVideoUrlFromDetail(det, null) ||
      pickPlaybackVideoUrlFromDetail(row, null);
    if (formVideoUrl || playbackUrl) return { formVideoUrl, playbackUrl };
  }
  return { formVideoUrl: '', playbackUrl: '' };
};

const getSiblingOrderNumber = (node) => {
  if (!node) return 0;
  const order =
    node.sibling_order ??
    node.procedure_tree?.sibling_order ??
    node.detail?.procedure_tree?.sibling_order ??
    '0';
  const parsed = parseInt(order, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortBySiblingOrder = (items = []) =>
  [...items].sort((a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b));

const childTreeTypeMatchesNodeType = (treeChild, nodeType) => {
  const t = (treeChild?.type || '').toLowerCase();
  if (nodeType === 'canvas' || nodeType === 'canva')
    return t === 'canvas' || t === 'canva';
  if (nodeType === 'rich-text') return t === 'rich-text' || t === 'rich_text';
  return t === nodeType;
};

/**
 * UPDATE_RICH_TEXT / similar audits often have no element ids in version_content — match
 * "Rich Text N" / "Attachment N" to the Nth sibling of that type under a step (display order).
 */
const parseChildOrdinalFromBarTitle = (nodeType, title) => {
  if (!title || typeof title !== 'string') return null;
  const s = title.trim();
  if (nodeType === 'rich-text') {
    const m = s.match(/(?:rich\s*text|rich)\s+(\d+)/i);
    if (m) {
      const n = parseInt(m[1], 10);
      return Number.isNaN(n) ? null : n;
    }
  }
  if (nodeType === 'attachment') {
    const m = s.match(/^attachment\s+(\d+)/i);
    if (m) {
      const n = parseInt(m[1], 10);
      return Number.isNaN(n) ? null : n;
    }
  }
  if (nodeType === 'video') {
    const m = s.match(/^video\s+(\d+)/i);
    if (m) {
      const n = parseInt(m[1], 10);
      return Number.isNaN(n) ? null : n;
    }
  }
  if (nodeType === 'canvas' || nodeType === 'canva') {
    const m = s.match(/^canvas\s+(\d+)/i);
    if (m) {
      const n = parseInt(m[1], 10);
      return Number.isNaN(n) ? null : n;
    }
  }
  if (nodeType === 'form') {
    const m = s.match(/^form\s+(\d+)/i);
    if (m) {
      const n = parseInt(m[1], 10);
      return Number.isNaN(n) ? null : n;
    }
  }
  return null;
};

const findChildInSectionsByBarTitleOrdinal = (sections, sourceNode) => {
  const title =
    sourceNode?.nodeTitle ||
    (typeof sourceNode?.detail?.action_to_show === 'string'
      ? sourceNode.detail.action_to_show
      : null);
  if (!Array.isArray(sections) || !title || !sourceNode?.nodeType) return null;
  const ord = parseChildOrdinalFromBarTitle(sourceNode.nodeType, title);
  if (ord == null) return null;
  const idx = ord - 1;
  for (const sec of sections) {
    for (const st of sec.steps || []) {
      const pool = sortBySiblingOrder(
        (st.children || []).filter((c) => childTreeTypeMatchesNodeType(c, sourceNode.nodeType)),
      );
      if (idx >= 0 && idx < pool.length) {
        return {
          sourceSection: sec,
          sourceStep: st,
          sourceParent: st,
          srcChild: pool[idx],
        };
      }
      for (const ss of st.substeps || []) {
        const subPool = sortBySiblingOrder(
          (ss.children || []).filter((c) =>
            childTreeTypeMatchesNodeType(c, sourceNode.nodeType),
          ),
        );
        if (idx >= 0 && idx < subPool.length) {
          return {
            sourceSection: sec,
            sourceStep: st,
            sourceParent: ss,
            srcChild: subPool[idx],
          };
        }
      }
    }
  }
  return null;
};

const getChildNodeId = (child) => {
  if (!child) return null;
  if (child.tree_node_id) return child.tree_node_id;
  if (child.node_id) return child.node_id;
  if (child.sub_step_tree_node_id) return child.sub_step_tree_node_id;
  const detail = child.detail || {};
  for (const field of CHILD_NODE_ID_FIELDS) {
    if (detail[field]) {
      return detail[field];
    }
  }
  return null;
};

/** When `srcChild.detail` is sparse, find the same video node elsewhere in the source tree. */
const findVideoDetailFromSourceTree = (sections, srcChild) => {
  if (!srcChild || !Array.isArray(sections)) return null;
  const matchIds = new Set(
    [
      srcChild.tree_node_id,
      srcChild.detail?.video_tree_node_id,
      srcChild.detail?.procedure_tree_ref,
      getChildNodeId(srcChild),
    ].filter(Boolean),
  );
  let bestDetail = null;
  let bestScore = 0;
  const visit = (c) => {
    if (!c || String(c.type || '').toLowerCase() !== 'video') return;
    const ids = [
      c.tree_node_id,
      c.detail?.video_tree_node_id,
      c.detail?.procedure_tree_ref,
      getChildNodeId(c),
    ].filter(Boolean);
    if (!ids.some((id) => matchIds.has(id))) return;
    const d = c.detail || {};
    const url = pickPlaybackVideoUrlFromDetail(d, null);
    const score = url ? url.length : 0;
    if (score > bestScore) {
      bestScore = score;
      bestDetail = d;
    } else if (!bestDetail && Object.keys(d).length > 0) {
      bestDetail = d;
    }
  };
  for (const sec of sections) {
    (sec.children || []).forEach(visit);
    for (const st of sec.steps || []) {
      (st.children || []).forEach(visit);
      for (const ss of st.substeps || []) {
        (ss.children || []).forEach(visit);
      }
    }
  }
  return bestDetail;
};

const resolveNodeNameWithFallback = (indexMap, id, node, defaultLabel) => {
  if (id && indexMap && indexMap[id]) {
    return indexMap[id];
  }
  if (node) {
    const candidateFields = [
      'title',
      'name',
      'label',
      'display_name',
      'section_name',
      'step_name',
      'substep_name',
    ];
    for (const field of candidateFields) {
      if (node[field]) {
        return node[field];
      }
    }
    if (node.detail) {
      for (const field of candidateFields) {
        if (node.detail[field]) {
          return node.detail[field];
        }
      }
    }
    if (node.type) {
      const type = node.type.toString();
      return type.charAt(0).toUpperCase() + type.slice(1);
    }
  }
  return defaultLabel;
};

const collectHierarchyNodesForCopyIndexing = (sections = [], indexMap = {}) => {
  const nodes = [];

  const addNode = (entry) => {
    const { id } = entry;
    if (!id) return;
    if (nodes.some((existing) => existing.id === id)) return;
    nodes.push(entry);
  };

  const sortedSections = sortBySiblingOrder(sections);
  sortedSections.forEach((section) => {
    const sectionId = section?.tree_node_id || section?.section_id_ref;
    const sectionLabel = resolveNodeNameWithFallback(indexMap, sectionId, section, 'Section');
    addNode({ id: sectionId, label: sectionLabel, type: 'section' });

    const sectionChildren = sortBySiblingOrder(section?.children || []);
    sectionChildren.forEach((child) => {
      const childId = getChildNodeId(child);
      const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
      addNode({
        id: childId,
        label: childLabel,
        type: 'sectionChild',
        parentSectionLabel: sectionLabel,
      });
    });

    const sortedSteps = sortBySiblingOrder(section?.steps || []);
    sortedSteps.forEach((step) => {
      const stepId = step?.tree_node_id || step?.step_id_ref;
      const stepLabel = resolveNodeNameWithFallback(indexMap, stepId, step, 'Step');
      addNode({
        id: stepId,
        label: stepLabel,
        type: 'step',
        parentSectionLabel: sectionLabel,
      });

      const stepChildren = sortBySiblingOrder(step?.children || []);
      stepChildren.forEach((child) => {
        const childId = getChildNodeId(child);
        const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
        addNode({
          id: childId,
          label: childLabel,
          type: 'stepChild',
          parentSectionLabel: sectionLabel,
          parentStepLabel: stepLabel,
        });
      });

      const sortedSubsteps = sortBySiblingOrder(step?.substeps || []);
      sortedSubsteps.forEach((substep) => {
        const substepId = substep?.tree_node_id || substep?.substep_id_ref;
        const substepLabel = resolveNodeNameWithFallback(indexMap, substepId, substep, 'Substep');
        addNode({
          id: substepId,
          label: substepLabel,
          type: 'substep',
          parentSectionLabel: sectionLabel,
          parentStepLabel: stepLabel,
        });

        const substepChildren = sortBySiblingOrder(substep?.children || []);
        substepChildren.forEach((child) => {
          const childId = getChildNodeId(child);
          const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
          addNode({
            id: childId,
            label: childLabel,
            type: 'substepChild',
            parentSectionLabel: sectionLabel,
            parentStepLabel: stepLabel,
            parentSubstepLabel: substepLabel,
          });
        });
      });
    });
  });

  return nodes;
};

// Helper to find a node in sourceNodes by ID
const findNodeInSource = (sourceNodes, nodeId, type) => {
  for (const section of sourceNodes) {
    if ((section.tree_node_id === nodeId || section.section_id_ref === nodeId) && type === 'section') {
      return section;
    }
    
    // Check section children
    if (section.children) {
      for (const child of section.children) {
        const childId = getChildNodeId(child);
        if (childId === nodeId && type === 'sectionChild') {
          return child;
        }
      }
    }
    
    // Check steps
    if (section.steps) {
      for (const step of section.steps) {
        if ((step.tree_node_id === nodeId || step.step_id_ref === nodeId) && type === 'step') {
          return step;
        }
        
        // Check step children
        if (step.children) {
          for (const child of step.children) {
            const childId = getChildNodeId(child);
            if (childId === nodeId && type === 'stepChild') {
              return child;
            }
          }
        }
        
        // Check substeps
        if (step.substeps) {
          for (const substep of step.substeps) {
            if ((substep.tree_node_id === nodeId || substep.substep_id_ref === nodeId) && type === 'substep') {
              return substep;
            }
            
            // Check substep children
            if (substep.children) {
              for (const child of substep.children) {
                const childId = getChildNodeId(child);
                if (childId === nodeId && type === 'substepChild') {
                  return child;
                }
              }
            }
          }
        }
      }
    }
  }
  return null;
};

// Helper to strip out children that haven't been added yet (for incremental building)
const stripUnaddedChildren = (node, type) => {
  const stripped = JSON.parse(JSON.stringify(node));
  
  // For sections: remove steps, substeps, and children (they'll be added in separate operations)
  if (type === 'section') {
    delete stripped.steps;
    delete stripped.children;
  }
  
  // For steps: remove substeps and children (they'll be added in separate operations)
  if (type === 'step') {
    delete stripped.substeps;
    delete stripped.children;
  }
  
  // For substeps: remove children (they'll be added in separate operations)
  if (type === 'substep') {
    delete stripped.children;
  }
  
  // For children nodes: they don't have nested children, so no stripping needed
  
  return stripped;
};

// Helper to add a node to the current structure incrementally
const addNodeToStructure = (currentStructure, nodeToAdd, hierarchyNode, sourceNodes) => {
  const clonedStructure = JSON.parse(JSON.stringify(currentStructure));
  const { type, id } = hierarchyNode;
  
  const nodeData = findNodeInSource(sourceNodes, id, type);
  if (!nodeData) return clonedStructure;
  
  // Clone the node but strip out children that haven't been added yet
  const clonedNode = stripUnaddedChildren(nodeData, type);
  
  if (type === 'section') {
    // Add section at the end (without steps/children - they'll be added separately)
    clonedStructure.push(clonedNode);
    // Normalize sibling orders
    clonedStructure.forEach((section, index) => {
      section.sibling_order = index.toString();
    });
  } else if (type === 'sectionChild') {
    // Find parent section in currentStructure (look for the section that was added from source)
    const parentSection = clonedStructure.find(sec => 
      sec.tree_node_id === hierarchyNode.parentSectionSourceId || 
      sec.section_id_ref === hierarchyNode.parentSectionSourceId ||
      sec.tree_node_id === hierarchyNode.parentSectionId || 
      sec.section_id_ref === hierarchyNode.parentSectionId
    );
    if (parentSection) {
      if (!parentSection.children) parentSection.children = [];
      parentSection.children.push(clonedNode);
      // Normalize sibling orders
      parentSection.children.forEach((child, index) => {
        child.sibling_order = index.toString();
      });
    }
  } else if (type === 'step') {
    // Find parent section in currentStructure
    const parentSection = clonedStructure.find(sec => 
      sec.tree_node_id === hierarchyNode.parentSectionSourceId || 
      sec.section_id_ref === hierarchyNode.parentSectionSourceId ||
      sec.tree_node_id === hierarchyNode.parentSectionId || 
      sec.section_id_ref === hierarchyNode.parentSectionId
    );
    if (parentSection) {
      if (!parentSection.steps) parentSection.steps = [];
      parentSection.steps.push(clonedNode);
      // Normalize sibling orders
      parentSection.steps.forEach((step, index) => {
        step.sibling_order = index.toString();
      });
    }
  } else if (type === 'stepChild') {
    // Find parent step in currentStructure
    for (const section of clonedStructure) {
      if (section.steps) {
        const parentStep = section.steps.find(st => 
          st.tree_node_id === hierarchyNode.parentStepSourceId || 
          st.step_id_ref === hierarchyNode.parentStepSourceId ||
          st.tree_node_id === hierarchyNode.parentStepId || 
          st.step_id_ref === hierarchyNode.parentStepId
        );
        if (parentStep) {
          if (!parentStep.children) parentStep.children = [];
          parentStep.children.push(clonedNode);
          // Normalize sibling orders
          parentStep.children.forEach((child, index) => {
            child.sibling_order = index.toString();
          });
          break;
        }
      }
    }
  } else if (type === 'substep') {
    // Find parent step in currentStructure
    for (const section of clonedStructure) {
      if (section.steps) {
        const parentStep = section.steps.find(st => 
          st.tree_node_id === hierarchyNode.parentStepSourceId || 
          st.step_id_ref === hierarchyNode.parentStepSourceId ||
          st.tree_node_id === hierarchyNode.parentStepId || 
          st.step_id_ref === hierarchyNode.parentStepId
        );
        if (parentStep) {
          if (!parentStep.substeps) parentStep.substeps = [];
          parentStep.substeps.push(clonedNode);
          // Normalize sibling orders
          parentStep.substeps.forEach((substep, index) => {
            substep.sibling_order = index.toString();
          });
          break;
        }
      }
    }
  } else if (type === 'substepChild') {
    // Find parent substep in currentStructure
    for (const section of clonedStructure) {
      if (section.steps) {
        for (const step of section.steps) {
          if (step.substeps) {
            const parentSubstep = step.substeps.find(ss => 
              ss.tree_node_id === hierarchyNode.parentSubstepSourceId || 
              ss.substep_id_ref === hierarchyNode.parentSubstepSourceId ||
              ss.tree_node_id === hierarchyNode.parentSubstepId || 
              ss.substep_id_ref === hierarchyNode.parentSubstepId
            );
            if (parentSubstep) {
              if (!parentSubstep.children) parentSubstep.children = [];
              parentSubstep.children.push(clonedNode);
              // Normalize sibling orders
              parentSubstep.children.forEach((child, index) => {
                child.sibling_order = index.toString();
              });
              break;
            }
          }
        }
      }
    }
  }
  
  return clonedStructure;
};

const buildCopyReplaceNodeIndexing = ({
  sourceNodes = [],
  targetNodes = [],
  sourceDocument,
  targetDocument,
  startswith=0,
  actionToShow=''
}) => {
  if (!Array.isArray(sourceNodes) || sourceNodes.length === 0) {
    return {};
  }

  const indexMap = generateNodeIndexing(sourceNodes);
  const hierarchyNodes = collectHierarchyNodesForCopyIndexing(sourceNodes, indexMap);
  if (hierarchyNodes.length === 0) {
    return {};
  }

  const actionPrefix = actionToShow;
  const totalOperations = hierarchyNodes.length;
  const nodeIndexing = {};

  let currentStructure = [];
  const baseTargetSnapshot = extractVersionStructure(
    Array.isArray(targetNodes) ? targetNodes : []
  ) || [];
  let previousSnapshot = null;

  hierarchyNodes.forEach((hierarchyNode, index) => {
    const { id, label, type, parentSectionLabel, parentStepLabel, parentSubstepLabel } = hierarchyNode;
    
    // Store parent IDs for finding parent nodes in sourceNodes
    // These will be used to find the corresponding nodes in currentStructure
    if (type === 'sectionChild' || type === 'step' || type === 'substep') {
      // Find parent section ID from sourceNodes
      for (const section of sourceNodes) {
        if (type === 'sectionChild' && section.children) {
          const child = section.children.find(c => getChildNodeId(c) === id);
          if (child) {
            hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
            hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
            break;
          }
        } else if (type === 'step' && section.steps) {
          const step = section.steps.find(s => (s.tree_node_id === id || s.step_id_ref === id));
          if (step) {
            hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
            hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
            break;
          }
        } else if (type === 'substep' && section.steps) {
          for (const step of section.steps) {
            if (step.substeps) {
              const substep = step.substeps.find(ss => (ss.tree_node_id === id || ss.substep_id_ref === id));
              if (substep) {
                hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepSourceId = step.tree_node_id || step.step_id_ref;
                break;
              }
            }
          }
        }
      }
    }
    
    if (type === 'stepChild' || type === 'substepChild') {
      // Find parent step/substep IDs from sourceNodes
      for (const section of sourceNodes) {
        if (section.steps) {
          for (const step of section.steps) {
            if (type === 'stepChild' && step.children) {
              const child = step.children.find(c => getChildNodeId(c) === id);
              if (child) {
                hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepSourceId = step.tree_node_id || step.step_id_ref;
                break;
              }
            } else if (type === 'substepChild' && step.substeps) {
              for (const substep of step.substeps) {
                if (substep.children) {
                  const child = substep.children.find(c => getChildNodeId(c) === id);
                  if (child) {
                    hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                    hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                    hierarchyNode.parentSubstepId = substep.tree_node_id || substep.substep_id_ref;
                    hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
                    hierarchyNode.parentStepSourceId = step.tree_node_id || step.step_id_ref;
                    hierarchyNode.parentSubstepSourceId = substep.tree_node_id || substep.substep_id_ref;
                    break;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    let destinationDescription = '';
    const targetDescriptor = `${targetDocument?.partNumber || ''}, Rev ${targetDocument?.revision || ''}`;

    switch (type) {
      case 'section':
        destinationDescription = `${targetDescriptor}`;
        break;
      case 'step':
        destinationDescription = `${parentSectionLabel || 'the target section'} of ${targetDescriptor}`;
        break;
      case 'sectionChild':
        destinationDescription = `${parentSectionLabel || 'the target section'} of ${targetDescriptor}`;
        break;
      case 'stepChild':
        destinationDescription = `${parentStepLabel || 'the target step'} of ${targetDescriptor}`;
        break;
      case 'substep':
        destinationDescription = `${parentStepLabel || 'the target step'} of ${targetDescriptor}`;
        break;
      case 'substepChild':
        destinationDescription = `${parentSubstepLabel || 'the target substep'} of ${targetDescriptor}`;
        break;
      default:
        destinationDescription = targetDescriptor;
        break;
    }

    const title = `${actionPrefix} System Performs Operation ${index + 1 + startswith} of ${totalOperations+startswith}, Copy ${label} into ${destinationDescription}.`;
    
    const originalContent = previousSnapshot
      ? JSON.parse(JSON.stringify(previousSnapshot))
      : JSON.parse(JSON.stringify(baseTargetSnapshot));
    
    // Add the node to structure
    currentStructure = addNodeToStructure(currentStructure, null, hierarchyNode, sourceNodes);
    
    // Capture newContent (state after adding this node)
    const newContent = extractVersionStructure(currentStructure) || [];
    
    // Store in node_indexing with new structure
    nodeIndexing[id] = {
      title,
      originalContent: originalContent || [],
      newContent: JSON.parse(JSON.stringify(newContent)),
    };
    
    previousSnapshot = JSON.parse(JSON.stringify(newContent));
  });

  return nodeIndexing;
};

/**
 * CopyReplace Component
 * Main component for copy & replace functionality
 * Manages the flow: Select Target → Preview → Confirm
 */
const CopyReplace = ({ auditTrailData, auditTrailId, breadcrumbText }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    sourceDocument, 
    sourceNode,
    copyMode,
    pastePosition,
    targetDocument, 
    previewMode, 
    comment,
    originalData,
    newData,
    isLoading,
    isVersionHistory,
    versionId,
    breadcrumbText: reduxBreadcrumbText,
  } = useSelector(state => state.preview);
  
  const [step, setStep] = useState('select'); // 'select' | 'pastePosition' | 'preview' | 'confirm'
  const [error, setError] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [sourceDataCache, setSourceDataCache] = useState(null); // Cache version history source data
  const [fetchedAuditTrailData, setFetchedAuditTrailData] = useState(null); // Store fetched audit trail data
  
  // Use Redux breadcrumbText if available, otherwise use prop
  const effectiveBreadcrumbText = reduxBreadcrumbText || breadcrumbText;
  
  // Fetch audit trail data when auditTrailId is provided
  useEffect(() => {
    if (isVersionHistory && auditTrailId && !auditTrailData) {
      dispatch(getAuditTrailByid({ id: auditTrailId }))
        .unwrap()
        .then((response) => {
          const auditData = response.data?.results || response.results || response;
          setFetchedAuditTrailData(auditData);
        })
        .catch((err) => {
          console.error('Error fetching audit trail:', err);
          setError('Failed to load version history data');
        });
    }
  }, [isVersionHistory, auditTrailId, auditTrailData, dispatch]);
  
  // Use either passed auditTrailData or fetched one
  const activeAuditTrailData = auditTrailData || fetchedAuditTrailData;

  // Handle document selection from SelectRevisionTable
  const handleRevisionSelect = async ({ group, revision }) => {
    try {
      setError(null);
      dispatch(setLoading(true));
      
      const targetDoc = {
        partId: group.partId,
        revisionId: revision.id,
        partNumber: group.partNumber,
        revision: revision.label,
        type: group.type,
        description: group.description,
        status: group.status || 'Not Submitted',
        tree_node_id:revision?.full?.tree_node_details?.tree_node_id,
        parent_based_version: revision?.full?.based_on_version_ref,
      };

      if (!sourceDocument?.partId || !sourceDocument?.revisionId) {
        dispatch(showSnackbar({
          message: 'Missing source procedure (part or revision id). Close Copy and open it again from the audit preview so identifiers load.',
          type: 'error',
        }));
        dispatch(setLoading(false));
        return;
      }
      if (!targetDoc.partId || !targetDoc.revisionId) {
        dispatch(showSnackbar({
          message: 'Invalid target revision. Pick another row or refresh the list.',
          type: 'error',
        }));
        dispatch(setLoading(false));
        return;
      }
      
      dispatch(setTargetDocument(targetDoc));
      
      // Fetch original data for the target document
      const originalResponse = await dispatch(getRevAllNodes({ 
        partId: targetDoc.partId, 
        revisionId: targetDoc.revisionId 
      })).unwrap();
      
      dispatch(setOriginalData(originalResponse));
      
      // Fetch source data - use audit trail data for version history, otherwise fetch normally
      let sourceResponse;
      if (isVersionHistory && activeAuditTrailData) {
        const versionContent =
          mergeAuditTrailRowVersionContentDeep(activeAuditTrailData) ||
          mergeAuditVersionContent(activeAuditTrailData) ||
          activeAuditTrailData.version_content ||
          {};
        const rawData = pickVersionHistoryInlineTreeEnvelope(activeAuditTrailData);

        sourceResponse = await resolveVersionHistorySourceSections({
          auditRow: activeAuditTrailData,
          versionId,
          sourcePartId: sourceDocument.partId,
          sourceRevisionId: sourceDocument.revisionId,
          dispatch,
        });

        if (!sourceResponse || sourceResponse.length === 0) {
          if (rawData.length > 0) {
            const effectiveVid = String(
              versionId || activeAuditTrailData.version_id || '',
            ).trim();
            const payload = {
              move_copy_version_id_ref: versionContent.move_copy_version_id_ref || '',
              created_at: activeAuditTrailData.version_created_at || '',
              data: rawData,
              ...(effectiveVid ? { version_id: effectiveVid } : {}),
              ...(auditTrailId ? { audit_trail_id: auditTrailId } : {}),
            };
            const contentResponse = await dispatch(
              getAuditTrailByPreview(payload),
            ).unwrap();
            sourceResponse = extractSectionsFromNodeDataPreviewResponse(contentResponse);
            if (!sourceResponse.length) {
              console.error(
                'WARNING: node/node-data returned empty; source may be wrong era.',
                contentResponse,
              );
            }
          }
        } else {
        }

        setSourceDataCache(sourceResponse);
      } else {
        // Normal copy from current revision
        sourceResponse = await dispatch(getRevAllNodes({ 
          partId: sourceDocument.partId, 
          revisionId: sourceDocument.revisionId 
        })).unwrap();
      }
      
      // For node copy, go to paste position selector; for document copy, go to preview
      if (copyMode === 'node') {
        setStep('pastePosition');
      } else {
        // Compute new data (replace target with source structure)
        dispatch(setNewData(sourceResponse));
        setStep('preview');
      }
    } catch (err) {
      console.error('Error fetching document data:', err);
      setError('Failed to load document data. Please try again.');
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Handle preview mode toggle
  const handlePreviewModeChange = (mode) => {
    dispatch(setPreviewMode(mode));
  };

  // Handle comment change
  const handleCommentChange = (newComment) => {
    dispatch(setComment(newComment));
  };

  // Handle position selection from PastePositionSelector
  const handlePositionSelected = async (position, targetType, targetId, targetIndex) => {
    try {
      dispatch(setLoading(true));
      
      // Fetch source document - use cached data for version history or fetch normally
      let sourceResponse;
      if (isVersionHistory && sourceDataCache && sourceDataCache.length > 0) {
        sourceResponse = sourceDataCache;
      } else if (isVersionHistory && activeAuditTrailData) {
        const versionContent =
          mergeAuditTrailRowVersionContentDeep(activeAuditTrailData) ||
          mergeAuditVersionContent(activeAuditTrailData) ||
          activeAuditTrailData.version_content ||
          {};
        const rawData = pickVersionHistoryInlineTreeEnvelope(activeAuditTrailData);

        sourceResponse = await resolveVersionHistorySourceSections({
          auditRow: activeAuditTrailData,
          versionId,
          sourcePartId: sourceDocument.partId,
          sourceRevisionId: sourceDocument.revisionId,
          dispatch,
        });

        if (!sourceResponse || sourceResponse.length === 0) {
          if (rawData.length > 0) {
            const effectiveVid = String(
              versionId || activeAuditTrailData.version_id || '',
            ).trim();
            const payload = {
              move_copy_version_id_ref: versionContent.move_copy_version_id_ref || '',
              created_at: activeAuditTrailData.version_created_at || '',
              data: rawData,
              ...(effectiveVid ? { version_id: effectiveVid } : {}),
              ...(auditTrailId ? { audit_trail_id: auditTrailId } : {}),
            };
            const contentResponse = await dispatch(
              getAuditTrailByPreview(payload),
            ).unwrap();
            sourceResponse = extractSectionsFromNodeDataPreviewResponse(contentResponse);
            if (!sourceResponse.length) {
              console.error(
                'WARNING: node/node-data returned empty.',
                contentResponse,
              );
            }
          }
        } else {
        }

        setSourceDataCache(sourceResponse);
      } else {
        // Normal copy from current revision
        sourceResponse = await dispatch(getRevAllNodes({ 
          partId: sourceDocument.partId, 
          revisionId: sourceDocument.revisionId 
        })).unwrap();
      }
      
      // Validate that we have source data
      if (!sourceResponse || sourceResponse.length === 0) {
        console.error('CRITICAL: sourceResponse is empty after all attempts to fetch data');
        console.error('isVersionHistory:', isVersionHistory);
        console.error('sourceDataCache:', sourceDataCache);
        console.error('activeAuditTrailData:', activeAuditTrailData);
        dispatch(showSnackbar({ 
          message: 'Failed to load source data for copy operation. The version history content may be empty or invalid.', 
          type: 'error' 
        }));
        dispatch(setLoading(false));
        return;
      }
      
      // Prepare working copy of target data and sorted sections
      let newTargetData = JSON.parse(JSON.stringify(originalData || []));
      const sortedSections = [...newTargetData].sort((a, b) => {
        const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
        const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
        return orderA - orderB;
      });

      // Find the source node (section or step or substep)
      let sourceSection = null;
      let sourceStep = null;
      let sourceSubstep = null;
      
      // Helper function to match node IDs (handles multiple ID field variations)
      const matchNodeId = (node, targetId, ...possibleFields) => {
        if (!node || !targetId) return false;
        for (const field of possibleFields) {
          if (node[field] === targetId) return true;
        }
        return false;
      };
      
      if (sourceNode.nodeType === 'section') {
        // For section copy: find the section directly
        const sectionMatchId = getSectionSourceMatchId(sourceNode);
        sourceSection = sectionMatchId
          ? sourceResponse.find((section) =>
              sectionMatchesAuditSectionId(section, sectionMatchId),
            )
          : null;
        if (!sourceSection) {
          sourceSection = findSectionByFallbackTitle(sourceResponse, sourceNode);
        }

        if (!sourceSection) {
          console.error('Source section not found');
          console.error('Looking for section with nodeId:', sectionMatchId);
          console.error('Available sections:', sourceResponse.map(s => ({
            tree_node_id: s.tree_node_id,
            section_id_ref: s.section_id_ref,
            section_tree_node_id: s.section_tree_node_id
          })));
          dispatch(showSnackbar({ message: 'Source section not found', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
      } else if (sourceNode.nodeType === 'step') {
        // For step copy: find the section containing the step, then find the step
        const stepMatchId = getStepSourceMatchId(sourceNode);
        const sectionMatchId = getStepSectionMatchId(sourceNode);

        if (sectionMatchId) {
          sourceSection = sourceResponse.find((section) =>
            sectionMatchesAuditSectionId(section, sectionMatchId),
          );
        }

        if (!sourceSection && stepMatchId) {
          const byStep = findSectionAndStepByStepId(
            sourceResponse,
            stepMatchId,
          );
          if (byStep) {
            sourceSection = byStep.sourceSection;
            sourceStep = byStep.sourceStep;
          }
        }

        if (!sourceSection) {
          const fb = findStepByFallbackInSections(sourceResponse, sourceNode);
          if (fb) {
            sourceSection = fb.sourceSection;
            sourceStep = fb.sourceStep;
          }
        }

        if (!sourceSection) {
          console.error('Source section not found for step');
          console.error('Looking for section with sectionId:', sectionMatchId);
          console.error('Available sections:', sourceResponse.map(s => ({
            tree_node_id: s.tree_node_id,
            section_id_ref: s.section_id_ref,
            section_tree_node_id: s.section_tree_node_id
          })));
          dispatch(showSnackbar({ message: 'Source section not found', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }

        if (!sourceStep) {
          if (stepMatchId) {
            sourceStep = sourceSection.steps?.find((step) =>
              stepMatchesAuditStepId(step, stepMatchId),
            );
          }
          if (!sourceStep) {
            const fbStep = findStepByFallbackInSections(
              [sourceSection],
              sourceNode,
            );
            if (fbStep) sourceStep = fbStep.sourceStep;
          }
        }

        if (!sourceStep) {
          console.error('Source step not found');
          console.error('Looking for step with nodeId:', stepMatchId);
          console.error('Available steps:', sourceSection.steps?.map(s => ({
            tree_node_id: s.tree_node_id,
            step_id_ref: s.step_id_ref,
            step_tree_node_id: s.step_tree_node_id
          })));
          dispatch(showSnackbar({ message: 'Source step not found', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
      } else if (sourceNode.nodeType === 'substep') {
        // For substep copy: find section, step, then substep
        const substepMatchId = getSubstepSourceMatchId(sourceNode);
        const sectionMatchId = getSubstepSectionMatchId(sourceNode);
        const stepMatchId = getSubstepStepMatchId(sourceNode);

        if (sectionMatchId) {
          sourceSection = sourceResponse.find((section) =>
            sectionMatchesAuditSectionId(section, sectionMatchId),
          );
        }

        if (!sourceSection && substepMatchId) {
          const bySs = findSectionStepSubstepBySubstepId(
            sourceResponse,
            substepMatchId,
          );
          if (bySs) {
            sourceSection = bySs.sourceSection;
            sourceStep = bySs.sourceStep;
            sourceSubstep = bySs.sourceSubstep;
          }
        }

        // Additional fallback: try to find section by title if still not found
        if (!sourceSection) {
          const fb = findSubstepByFallbackInSections(sourceResponse, sourceNode);
          if (fb) {
            sourceSection = fb.sourceSection;
            sourceStep = fb.sourceStep;
            sourceSubstep = fb.sourceSubstep;
          }
        }

        // Final fallback: try to find by ordinal if title-based fallback failed
        if (!sourceSection && sourceNode) {
          // Try to match by substep ordinal across all sections
          for (let i = 1; i <= 10; i++) {
            const testNode = { ...sourceNode, nodeTitle: `Substep ${i}` };
            const fb = findSubstepByFallbackInSections(sourceResponse, testNode);
            if (fb) {
              sourceSection = fb.sourceSection;
              sourceStep = fb.sourceStep;
              sourceSubstep = fb.sourceSubstep;
              break;
            }
          }
        }

        if (!sourceSection) {
          console.error('Source section not found for substep');
          console.error('Looking for section with sectionId:', sectionMatchId);
          console.error('SubstepMatchId:', substepMatchId);
          console.error('StepMatchId:', stepMatchId);
          console.error('SourceNode:', sourceNode);
          console.error('Available sections:', sourceResponse.map(s => ({
            tree_node_id: s.tree_node_id,
            section_id_ref: s.section_id_ref,
            section_tree_node_id: s.section_tree_node_id,
            steps_count: s.steps?.length || 0
          })));
          dispatch(showSnackbar({ message: 'Source section not found', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        if (!sourceStep && stepMatchId) {
          sourceStep = sourceSection.steps?.find((step) =>
            stepMatchesAuditStepId(step, stepMatchId),
          );
        }
        if (!sourceStep) {
          console.error('Source step not found for substep');
          console.error('Looking for step with stepId:', stepMatchId);
          dispatch(showSnackbar({ message: 'Source step not found', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        if (!sourceSubstep && substepMatchId) {
          sourceSubstep = sourceStep.substeps?.find((ss) =>
            substepMatchesAuditSubstepId(ss, substepMatchId),
          );
        }
        if (!sourceSubstep) {
          console.error('Source substep not found');
          console.error('Looking for substep with nodeId:', substepMatchId);
          console.error('Available substeps:', sourceStep.substeps?.map(s => ({
            tree_node_id: s.tree_node_id,
            substep_id_ref: s.substep_id_ref,
            sub_step_tree_node_id: s.sub_step_tree_node_id,
            substep_tree_node_id: s.substep_tree_node_id
          })));
          dispatch(showSnackbar({ message: 'Source substep not found', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
      }
      // Child node copy (Attachment, Video, Rich Text, Canvas, Form)
      else if ([ 'attachment','video','rich-text','form','canva','canvas' ].includes(sourceNode.nodeType)) {
        // Resolve source child from source tree. Audit trail section_id_ref / step_id_ref may not match
        // getRevAllNodes ids; walk by child node id when the direct path fails.
        const childSourceNode = activeAuditTrailData
          ? enrichChildSourceNodeFromAuditRow(sourceNode, activeAuditTrailData, effectiveBreadcrumbText)
          : sourceNode;
        
        if (activeAuditTrailData?.version_content) {
        }
        const childSectionMatchId = getChildSectionMatchId(childSourceNode);
        const childStepMatchId = getChildStepMatchId(childSourceNode);

        // Log all available sections and their IDs
        sourceResponse.forEach((section, idx) => {
        });

        if (childSectionMatchId) {
          sourceSection = sourceResponse.find((sec) =>
            sectionMatchesAuditSectionId(sec, childSectionMatchId),
          );
        }
        if (sourceSection && childStepMatchId) {
          sourceSection.steps?.forEach((step, idx) => {
          });
          sourceStep = (sourceSection.steps || []).find((st) =>
            stepMatchesAuditStepId(st, childStepMatchId),
          );
        }
        if (!sourceSection && childStepMatchId) {
          const byStep = findSectionAndStepByStepId(
            sourceResponse,
            childStepMatchId,
          );
          if (byStep) {
            sourceSection = byStep.sourceSection;
            sourceStep = byStep.sourceStep;
          }
        }

        let sourceParent = sourceStep;
        if (sourceStep && childSourceNode.substepId) {
          const ss = (sourceStep.substeps || []).find(s => matchNodeId(s, childSourceNode.substepId, 'tree_node_id', 'substep_id_ref', 'sub_step_tree_node_id', 'substep_tree_node_id'));
          if (ss) sourceParent = ss;
        }

        let srcChildren = sourceParent ? sourceParent.children || [] : [];
        let srcChild = srcChildren.find(c => childMatchesSourceNode(c, childSourceNode));
        
        // Log the matching process in detail
        // Check what ID we're looking for
        const sourceId = getChildSourceMatchId(childSourceNode);
        // Check each child to see if any match
        if (srcChildren && srcChildren.length > 0) {
          srcChildren.forEach((child, idx) => {
            const matches = childMatchesSourceNode(child, childSourceNode);
          });
        } else {
        }
        if (srcChildren.length > 0) {
          srcChildren.forEach((child, idx) => {
          });
        }
        
        if (sourceParent === null) {
          // Global search: find the attachment anywhere in the source data
          outerFindChild: for (const sec of sourceResponse) {
            for (const st of (sec.steps || [])) {
              // Search in step children
              const inStep = (st.children || []).find(c => childMatchesSourceNode(c, childSourceNode));
              if (inStep) {
                sourceSection = sec;
                sourceStep = st;
                sourceParent = st;
                srcChildren = st.children || [];
                srcChild = inStep;
                break outerFindChild;
              }
              
              // Search in substep children
              for (const ss of (st.substeps || [])) {
                const inSubstep = (ss.children || []).find(c => childMatchesSourceNode(c, childSourceNode));
                if (inSubstep) {
                  sourceSection = sec;
                  sourceStep = st;
                  sourceParent = ss;
                  srcChildren = ss.children || [];
                  srcChild = inSubstep;
                  break outerFindChild;
                }
              }
            }
          }
          
          if (!srcChild) {
            // Extract node number from title and find by position (works for Attachment, Video, etc.)
            let titleMatch = null;
            if (childSourceNode?.nodeType === 'attachment') {
              titleMatch = childSourceNode?.nodeTitle?.match(/Attachment (\d+)/i);
            } else if (childSourceNode?.nodeType === 'video') {
              titleMatch = childSourceNode?.nodeTitle?.match(/Video (\d+)/i);
            } else if (childSourceNode?.nodeType === 'rich-text') {
              titleMatch = childSourceNode?.nodeTitle?.match(/Rich Text (\d+)/i);
            } else if (childSourceNode?.nodeType === 'canva' || childSourceNode?.nodeType === 'canvas') {
              titleMatch = childSourceNode?.nodeTitle?.match(/Canvas (\d+)/i);
            } else if (childSourceNode?.nodeType === 'form') {
              titleMatch = childSourceNode?.nodeTitle?.match(/Form (\d+)/i);
            }
            const targetPosition = titleMatch ? parseInt(titleMatch[1]) : null;
            
            if (targetPosition) {
              let currentPosition = 0;
              positionSearch: for (const sec of sourceResponse) {
                for (const st of (sec.steps || [])) {
                  // Count step children
                  for (const child of (st.children || [])) {
                    if (child.type === childSourceNode?.nodeType) {
                      currentPosition++;
                      if (currentPosition === targetPosition) {
                        sourceSection = sec;
                        sourceStep = st;
                        sourceParent = st;
                        srcChildren = st.children || [];
                        srcChild = child;
                        break positionSearch;
                      }
                    }
                  }
                  
                  // Count substep children
                  for (const ss of (st.substeps || [])) {
                    for (const child of (ss.children || [])) {
                      if (child.type === childSourceNode?.nodeType) {
                        currentPosition++;
                        if (currentPosition === targetPosition) {
                          sourceSection = sec;
                          sourceStep = st;
                          sourceParent = ss;
                          srcChildren = ss.children || [];
                          srcChild = child;
                          break positionSearch;
                        }
                      }
                    }
                  }
                }
              }
              
              if (!srcChild) {
              }
            }
          }
        } else if (srcChildren.length === 0) {
        } else if (!srcChild) {
          outerFindChild: for (const sec of sourceResponse) {
            for (const st of (sec.steps || [])) {
              const inStep = (st.children || []).find(c => childMatchesSourceNode(c, childSourceNode));
              if (inStep) {
                sourceSection = sec;
                sourceStep = st;
                sourceParent = st;
                srcChildren = st.children || [];
                srcChild = inStep;
                break outerFindChild;
              }
              for (const ss of (st.substeps || [])) {
                const inSub = (ss.children || []).find(c => childMatchesSourceNode(c, childSourceNode));
                if (inSub) {
                  sourceSection = sec;
                  sourceStep = st;
                  sourceParent = ss;
                  srcChildren = ss.children || [];
                  srcChild = inSub;
                  break outerFindChild;
                }
              }
            }
          }
        }

        if (!srcChild) {
          const byTitle = findChildInSectionsByBarTitleOrdinal(
            sourceResponse,
            childSourceNode,
          );
          if (byTitle) {
            sourceSection = byTitle.sourceSection;
            sourceStep = byTitle.sourceStep;
            sourceParent = byTitle.sourceParent;
            srcChildren = sourceParent.children || [];
            srcChild = byTitle.srcChild;
          }
        }

        if (!srcChild) {
          // Additional fallback: try to find attachment by sibling_order if ID matching failed
          if (childSourceNode?.nodeType === 'attachment') {
            // First try to get sibling_order directly from childSourceNode
            let targetOrder = null;
            if (childSourceNode?.sibling_order) {
              targetOrder = parseInt(childSourceNode.sibling_order);
            } else if (activeAuditTrailData?.version_content?.sibling_order) {
              // Try to get sibling_order from audit trail data
              targetOrder = parseInt(activeAuditTrailData.version_content.sibling_order);
            } else {
              // Fallback: Extract the number from the title (e.g., "Attachment 6" -> 6)
              const titleMatch = childSourceNode?.nodeTitle?.match(/Attachment (\d+)/i);
              targetOrder = titleMatch ? parseInt(titleMatch[1]) : null;
            }
            
            if (activeAuditTrailData?.version_content) {
            }
            // Count all attachments in source
            let totalAttachments = 0;
            sourceResponse.forEach((sec, secIdx) => {
              (sec.steps || []).forEach((st, stepIdx) => {
                const stepAttachments = (st.children || []).filter(c => c.type === 'attachment');
                totalAttachments += stepAttachments.length;
                stepAttachments.forEach((att, attIdx) => {
                });
                
                (st.substeps || []).forEach((ss, subIdx) => {
                  const subAttachments = (ss.children || []).filter(c => c.type === 'attachment');
                  totalAttachments += subAttachments.length;
                  subAttachments.forEach((att, attIdx) => {
                  });
                });
              });
            });
            if (targetOrder) {
              for (const sec of sourceResponse) {
                for (const st of (sec.steps || [])) {
                  const children = st.children || [];
                  children.forEach((c, idx) => {
                    if (c.type === 'attachment') {
                    }
                  });
                  const byOrder = children.find(c => 
                    c.type === 'attachment' && 
                    c.sibling_order === targetOrder.toString()
                  );
                  if (byOrder) {
                    sourceSection = sec;
                    sourceStep = st;
                    sourceParent = st;
                    srcChildren = children;
                    srcChild = byOrder;
                    break;
                  }
                  
                  // Also check substeps
                  for (const ss of (st.substeps || [])) {
                    const subChildren = ss.children || [];
                    subChildren.forEach((c, idx) => {
                      if (c.type === 'attachment') {
                      }
                    });
                    const byOrderInSub = subChildren.find(c => 
                      c.type === 'attachment' && 
                      c.sibling_order === targetOrder.toString()
                    );
                    if (byOrderInSub) {
                      sourceSection = sec;
                      sourceStep = st;
                      sourceParent = ss;
                      srcChildren = subChildren;
                      srcChild = byOrderInSub;
                      break;
                    }
                  }
                  if (srcChild) break;
                }
                if (srcChild) break;
              }
            }
          }
        }

        if (!srcChild) {
          console.error('Source child not found for child copy');
          console.error(
            'Looking for sectionId:',
            childSectionMatchId,
            'stepId:',
            childStepMatchId,
            'childId:',
            getChildSourceMatchId(childSourceNode),
          );
          dispatch(showSnackbar({ message: 'Source child not found for copy.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        if (!sourceSection || !sourceStep) {
          console.error('Source section/step missing after resolving child');
          dispatch(showSnackbar({ message: 'Source section not found for child', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        const clonedChild = JSON.parse(JSON.stringify(srcChild));

        // Insert into target
        if (position === 'into' && targetType === 'step') {
          const tSection = (sortedSections || []).find(sec => (sec.steps || []).some(st => st.tree_node_id === targetId));
          if (!tSection) { dispatch(showSnackbar({ message: 'Target step section not found', type: 'error' })); return; }
          const tStep = (tSection.steps || []).find(st => st.tree_node_id === targetId);
          if (!tStep) { dispatch(showSnackbar({ message: 'Target step not found', type: 'error' })); return; }
          const children = tStep.children || [];
          const sortedChildren = [...children].sort((a,b)=>{
            const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
            const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
            return aO - bO;
          });
          const maxO = sortedChildren.length ? parseInt(sortedChildren[sortedChildren.length-1].sibling_order || sortedChildren[sortedChildren.length-1].procedure_tree?.sibling_order || '0',10) : 0;
          clonedChild.sibling_order = (maxO + 1).toString();
          sortedChildren.push(clonedChild);
          sortedChildren.forEach((c,i)=>{ c.sibling_order = i.toString(); });
          tStep.children = sortedChildren;
          newTargetData = sortedSections;
        } else if (position === 'into' && targetType === 'substep') {
          const tSection = (sortedSections || []).find(sec => (sec.steps || []).some(st => (st.substeps || []).some(ss => ss.tree_node_id === targetId)));
          if (!tSection) { dispatch(showSnackbar({ message: 'Target substep section not found', type: 'error' })); return; }
          const tStep = (tSection.steps || []).find(st => (st.substeps || []).some(ss => ss.tree_node_id === targetId));
          const tSubstep = (tStep?.substeps || []).find(ss => ss.tree_node_id === targetId);
          if (!tStep || !tSubstep) { dispatch(showSnackbar({ message: 'Target substep not found', type: 'error' })); return; }
          const children = tSubstep.children || [];
          const sortedChildren = [...children].sort((a,b)=>{
            const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
            const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
            return aO - bO;
          });
          const maxO = sortedChildren.length ? parseInt(sortedChildren[sortedChildren.length-1].sibling_order || sortedChildren[sortedChildren.length-1].procedure_tree?.sibling_order || '0',10) : 0;
          clonedChild.sibling_order = (maxO + 1).toString();
          sortedChildren.push(clonedChild);
          sortedChildren.forEach((c,i)=>{ c.sibling_order = i.toString(); });
          tSubstep.children = sortedChildren;
          newTargetData = sortedSections;
        } else if ((position === 'above' || position === 'below') && targetType === 'child') {
          // find parent that contains target child
          let parentContainer = null;
          let list = null;
          outer: for (const sec of (sortedSections || [])) {
            for (const st of (sec.steps || [])) {
              const stepChildren = st.children || [];
              // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
              const idxS = stepChildren.findIndex(c => {
                const nodeId = c.tree_node_id;
                const canvasNodeId = c.detail?.canvas_tree_node_id;
                return nodeId === targetId || canvasNodeId === targetId;
              });
              if (idxS !== -1) { parentContainer = st; list = stepChildren; break outer; }
              for (const ss of (st.substeps || [])) {
                const subChildren = ss.children || [];
                // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                const idxSS = subChildren.findIndex(c => {
                  const nodeId = c.tree_node_id;
                  const canvasNodeId = c.detail?.canvas_tree_node_id;
                  return nodeId === targetId || canvasNodeId === targetId;
                });
                if (idxSS !== -1) { parentContainer = ss; list = subChildren; break outer; }
              }
            }
          }
          if (!parentContainer || !list) { dispatch(showSnackbar({ message: 'Target child not found', type: 'error' })); return; }
          const sortedChildren = [...list].sort((a,b)=>{
            const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
            const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
            return aO - bO;
          });
          // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
          const tIdx = sortedChildren.findIndex(c => {
            const nodeId = c.tree_node_id;
            const canvasNodeId = c.detail?.canvas_tree_node_id;
            return nodeId === targetId || canvasNodeId === targetId;
          });
          if (tIdx === -1) { dispatch(showSnackbar({ message: 'Target child not found in sorted list', type: 'error' })); return; }
          if (position === 'above') sortedChildren.splice(tIdx, 0, clonedChild); else sortedChildren.splice(tIdx + 1, 0, clonedChild);
          sortedChildren.forEach((c,i)=>{ c.sibling_order = i.toString(); });
          parentContainer.children = sortedChildren;
          newTargetData = sortedSections;
        }
      }
      
      // Get a deep clone of the source node (section or step or substep)
      let clonedNode;
      if (sourceNode.nodeType === 'section') {
        clonedNode = JSON.parse(JSON.stringify(sourceSection));
      } else if (sourceNode.nodeType === 'step') {
        clonedNode = JSON.parse(JSON.stringify(sourceStep));
      } else if (sourceNode.nodeType === 'substep') {
        clonedNode = JSON.parse(JSON.stringify(sourceSubstep));
      }
      
      

      // Compute new data based on paste position and node type
      if (sourceNode.nodeType === 'section') {
        // Section copy logic
        const clonedSection = clonedNode;
        if (position === 'into') {
          // Paste into document (add as new section at the end)
          // Update sibling_order to be after all existing sections
          const maxOrder = sortedSections.length > 0 
            ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
            : 0;
          clonedSection.sibling_order = (maxOrder + 1).toString();
          sortedSections.push(clonedSection);
          // Reassign sibling orders sequentially
          sortedSections.forEach((section, index) => {
            section.sibling_order = index.toString();
          });
          newTargetData = sortedSections;
        } else if (position === 'above') {
          // Paste above selected section - find target in sorted array
          const targetSectionIndex = sortedSections.findIndex(
            section => section.tree_node_id === targetId
          );
          
          if (targetSectionIndex !== -1) {
            // Insert before target section
            sortedSections.splice(targetSectionIndex, 0, clonedSection);
            
            // Reassign sibling orders sequentially (0, 1, 2, ...)
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          } else {
            // Target not found, add to end
            const maxOrder = sortedSections.length > 0 
              ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            clonedSection.sibling_order = (maxOrder + 1).toString();
            sortedSections.push(clonedSection);
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          }
        } else if (position === 'below') {
          // Paste below selected section - find target in sorted array
          const targetSectionIndex = sortedSections.findIndex(
            section => section.tree_node_id === targetId
          );
          
          if (targetSectionIndex !== -1) {
            // Insert after target section
            sortedSections.splice(targetSectionIndex + 1, 0, clonedSection);
            
            // Reassign sibling orders sequentially (0, 1, 2, ...)
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          } else {
            // Target not found, add to end
            const maxOrder = sortedSections.length > 0 
              ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            clonedSection.sibling_order = (maxOrder + 1).toString();
            sortedSections.push(clonedSection);
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          }
        }
      } else if (sourceNode.nodeType === 'step') {
        // Step copy logic
        const clonedStep = clonedNode;
        
        if (position === 'into') {
          // Paste step into section
          const targetSectionIndex = sortedSections.findIndex(
            section => section.tree_node_id === targetId
          );
          
          if (targetSectionIndex !== -1) {
            const targetSection = sortedSections[targetSectionIndex];
            const targetSteps = targetSection.steps || [];
            
            // Sort steps by sibling_order
            const sortedSteps = [...targetSteps].sort((a, b) => {
              const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
              const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
              return orderA - orderB;
            });
            
            // Get max order and add 1
            const maxOrder = sortedSteps.length > 0
              ? parseInt(sortedSteps[sortedSteps.length - 1].sibling_order || sortedSteps[sortedSteps.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            
            clonedStep.sibling_order = (maxOrder + 1).toString();
            sortedSteps.push(clonedStep);
            
            // Reassign sibling orders sequentially
            sortedSteps.forEach((step, index) => {
              step.sibling_order = index.toString();
            });
            
            // Update the target section with new steps
            targetSection.steps = sortedSteps;
            newTargetData = sortedSections;
          } else {
            // Target section not found
            dispatch(showSnackbar({ message: 'Target section not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        } else if (position === 'above' || position === 'below') {
          // Paste step above or below another step
          // Find the target step's section first
          let targetStepSection = null;
          let targetStep = null;
          let targetStepSectionIndex = -1;
          let targetStepIndex = -1;
          
          for (let i = 0; i < sortedSections.length; i++) {
            const section = sortedSections[i];
            const steps = section.steps || [];
            const stepIndex = steps.findIndex(step => step.tree_node_id === targetId);
            
            if (stepIndex !== -1) {
              targetStepSection = section;
              targetStep = steps[stepIndex];
              targetStepSectionIndex = i;
              targetStepIndex = stepIndex;
              break;
            }
          }
          
          if (!targetStepSection || !targetStep) {
            dispatch(showSnackbar({ message: 'Target step not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          
          // Get all steps from target section and sort them
          const allSteps = targetStepSection.steps || [];
          const sortedSteps = [...allSteps].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
            const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
            return orderA - orderB;
          });
          
          // Find target step index in sorted array
          const sortedTargetStepIndex = sortedSteps.findIndex(
            step => step.tree_node_id === targetId
          );
          
          if (sortedTargetStepIndex !== -1) {
            if (position === 'above') {
              // Insert before target step
              sortedSteps.splice(sortedTargetStepIndex, 0, clonedStep);
            } else {
              // Insert after target step
              sortedSteps.splice(sortedTargetStepIndex + 1, 0, clonedStep);
            }
            
            // Reassign sibling orders sequentially
            sortedSteps.forEach((step, index) => {
              step.sibling_order = index.toString();
            });
            
            // Update the target section with new steps
            targetStepSection.steps = sortedSteps;
            newTargetData = sortedSections;
          } else {
            dispatch(showSnackbar({ message: 'Target step not found in sorted array', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        }
      } else if (sourceNode.nodeType === 'substep') {
        // Substep copy logic
        const clonedSubstep = clonedNode;

        if (position === 'into' && targetType === 'step') {
          // Paste substep into a step
          // Find target step
          let targetSection = (sortedSections || []).filter(Boolean).find(section => (section.steps || []).some(st => st.tree_node_id === targetId));
          if (!targetSection) {
            dispatch(showSnackbar({ message: 'Target step section not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          const targetStep = (targetSection.steps || []).find(st => st.tree_node_id === targetId);
          if (!targetStep) {
            dispatch(showSnackbar({ message: 'Target step not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }

          const substeps = targetStep.substeps || [];
          const sortedSubsteps = [...substeps].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
            const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
            return orderA - orderB;
          });
          const maxOrder = sortedSubsteps.length > 0
            ? parseInt(sortedSubsteps[sortedSubsteps.length - 1].sibling_order || sortedSubsteps[sortedSubsteps.length - 1].procedure_tree?.sibling_order || '0', 10)
            : 0;
          clonedSubstep.sibling_order = (maxOrder + 1).toString();
          sortedSubsteps.push(clonedSubstep);
          sortedSubsteps.forEach((ss, index) => {
            ss.sibling_order = index.toString();
          });
          targetStep.substeps = sortedSubsteps;
          newTargetData = sortedSections;
        } else if ((position === 'above' || position === 'below') && targetType === 'substep') {
          // Paste substep relative to another substep
          // Find the step containing the target substep
          let containingSection = null;
          let containingStep = null;
          for (let i = 0; i < (sortedSections || []).length; i++) {
            const section = sortedSections[i];
            if (!section) continue;
            for (let j = 0; j < ((section.steps || []).length); j++) {
              const step = section.steps[j];
              const idx = (step.substeps || []).findIndex(ss => ss.tree_node_id === targetId);
              if (idx !== -1) {
                containingSection = section;
                containingStep = step;
                break;
              }
            }
            if (containingStep) break;
          }
          if (!containingStep) {
            dispatch(showSnackbar({ message: 'Target substep not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }

          const substeps = containingStep.substeps || [];
          const sortedSubsteps = [...substeps].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
            const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
            return orderA - orderB;
          });
          const targetIdx = sortedSubsteps.findIndex(ss => ss.tree_node_id === targetId);
          if (targetIdx === -1) {
            dispatch(showSnackbar({ message: 'Target substep not found in sorted array', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          if (position === 'above') {
            sortedSubsteps.splice(targetIdx, 0, clonedSubstep);
          } else {
            sortedSubsteps.splice(targetIdx + 1, 0, clonedSubstep);
          }
          sortedSubsteps.forEach((ss, index) => {
            ss.sibling_order = index.toString();
          });
          containingStep.substeps = sortedSubsteps;
          newTargetData = sortedSections;
        }
      }
      
      // Dispatch the computed new data
      dispatch(setNewData(newTargetData));
      
      // Move to preview step
      setStep('preview');
    } catch (error) {
      console.error('Error computing new structure:', error);
      dispatch(showSnackbar({ message: 'Error computing preview: ' + (error?.message || 'Unknown error'), type: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (step === 'preview') {
      if (copyMode === 'node') {
        setStep('pastePosition');
      } else {
        setStep('select');
      }
    } else if (step === 'pastePosition') {
      setStep('select');
    } else if (step === 'confirm') {
      setStep('preview');
    }
  };

  // Handle close
  const handleClose = () => {
    dispatch(closeCopyReplace());
  };

  // Handle save confirmation - Copy & Replace operation
  const handleConfirmSave = async () => {
    if (!comment || !comment.trim()) {
      dispatch(showSnackbar({ message: 'Comment is mandatory.', type: 'error' }));
      return;
    }

    if (!sourceDocument || !targetDocument) {
      dispatch(showSnackbar({ message: 'Source or target document is missing.', type: 'error' }));
      return;
    }

    const childNodeKeys = [
      'attachment',
      'video',
      'rich-text',
      'rich_text',
      'richText',
      'form',
      'canva',
      'canvas',
    ];
    dispatch(setLoading(true));
    
    try {
      // For node copy (section, step, substep) — prefer sourceNode presence over copyMode to avoid misrouting
      if (sourceNode) {
        if (!sourceNode) {
          dispatch(showSnackbar({ message: 'Missing source node for copy.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        if (!pastePosition) {
          dispatch(showSnackbar({ message: 'Please select a paste position.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        if (!sourceDocument.partId || !sourceDocument.revisionId) {
          dispatch(showSnackbar({
            message: 'Missing source procedure (part or revision id). Close Copy and open it again from the audit preview.',
            type: 'error',
          }));
          dispatch(setLoading(false));
          return;
        }
        // Source tree for VH copy must match preview (snapshot / version id), not live revision only.
        let sourceAllNodes;
        if (isVersionHistory) {
          sourceAllNodes = await loadVersionHistorySourceTreeForConfirm({
            sourceDataCache,
            auditRow: activeAuditTrailData,
            versionId,
            sourcePartId: sourceDocument.partId,
            sourceRevisionId: sourceDocument.revisionId,
            auditTrailId,
            dispatch,
          });
          if (!sourceAllNodes || sourceAllNodes.length === 0) {
            dispatch(
              showSnackbar({
                message:
                  'Could not load version-history source for save. Close Copy and open it again from the audit preview.',
                type: 'error',
              }),
            );
            dispatch(setLoading(false));
            return;
          }
        } else {
          sourceAllNodes = await dispatch(
            getRevAllNodes({
              partId: sourceDocument.partId,
              revisionId: sourceDocument.revisionId,
            }),
          ).unwrap();
        }

        // Declare variables used across all branches (including child) BEFORE any assignment
        let toOrder;
        let toProcedureTreeNodeRef;
        let toSectionIdRef;
        let toStepIdRef;
        let toSubstepIdRef;
        let direction;
        let actionType;
        let commentText;
        let fromType;
        let fromTreeId;
        let fromOrder;
        let fromSectionId;
        let fromStepId;
        let fromSubstepId;
        let fromChildNodeRef;
        let toChildType; // Store target child type for paste above/below

        // Get target document data early for use in all branches
        const targetAllNodes =
          Array.isArray(originalData) && originalData.length > 0
            ? originalData
            : await dispatch(
                getRevAllNodes({
                  partId: targetDocument.partId,
                  revisionId: targetDocument.revisionId,
                }),
              ).unwrap();

        // Find source node (section or step or substep)
        let sourceSection = null;
        let sourceStep = null;
        let sourceSubstep = null;
        let srcChild = null;
        
        // Helper function to match node IDs (same as in handlePositionSelected)
        const matchNodeIdConfirm = (node, targetId, ...possibleFields) => {
          if (!node || !targetId) return false;
          for (const field of possibleFields) {
            if (node[field] === targetId) return true;
          }
          return false;
        };
        
        if (sourceNode.nodeType === 'section') {
          // For section copy: find the section directly
          const sectionMatchId = getSectionSourceMatchId(sourceNode);
          sourceSection = sectionMatchId
            ? sourceAllNodes.find((section) =>
                sectionMatchesAuditSectionId(section, sectionMatchId),
              )
            : null;
          if (!sourceSection) {
            sourceSection = findSectionByFallbackTitle(sourceAllNodes, sourceNode);
          }

          if (!sourceSection) {
            console.error('Source section not found in handleConfirm');
            console.error('Looking for section with nodeId:', sectionMatchId);
            dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        } else if (sourceNode.nodeType === 'step') {
          // For step copy: find the section containing the step, then find the step
          const stepMatchId = getStepSourceMatchId(sourceNode);
          const sectionMatchId = getStepSectionMatchId(sourceNode);

          if (sectionMatchId) {
            sourceSection = sourceAllNodes.find((section) =>
              sectionMatchesAuditSectionId(section, sectionMatchId),
            );
          }

          if (!sourceSection && stepMatchId) {
            const byStep = findSectionAndStepByStepId(
              sourceAllNodes,
              stepMatchId,
            );
            if (byStep) {
              sourceSection = byStep.sourceSection;
              sourceStep = byStep.sourceStep;
            }
          }

          if (!sourceSection) {
            const fb = findStepByFallbackInSections(sourceAllNodes, sourceNode);
            if (fb) {
              sourceSection = fb.sourceSection;
              sourceStep = fb.sourceStep;
            }
          }

          if (!sourceSection) {
            console.error('Source section not found for step in handleConfirm');
            console.error('Looking for section with sectionId:', sectionMatchId);
            dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }

          if (!sourceStep) {
            if (stepMatchId) {
              sourceStep = sourceSection.steps?.find((step) =>
                stepMatchesAuditStepId(step, stepMatchId),
              );
            }
            if (!sourceStep) {
              const fbStep = findStepByFallbackInSections(
                [sourceSection],
                sourceNode,
              );
              if (fbStep) sourceStep = fbStep.sourceStep;
            }
          }

          if (!sourceStep) {
            console.error('Source step not found in handleConfirm');
            console.error('Looking for step with nodeId:', stepMatchId);
            dispatch(showSnackbar({ message: 'Source step not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        } else if (sourceNode.nodeType === 'substep') {
          // For substep copy: find section and step, then substep
          const substepMatchId = getSubstepSourceMatchId(sourceNode);
          const sectionMatchId = getSubstepSectionMatchId(sourceNode);
          const stepMatchId = getSubstepStepMatchId(sourceNode);

          if (sectionMatchId) {
            sourceSection = sourceAllNodes.find((section) =>
              sectionMatchesAuditSectionId(section, sectionMatchId),
            );
          }

          if (!sourceSection && substepMatchId) {
            const bySs = findSectionStepSubstepBySubstepId(
              sourceAllNodes,
              substepMatchId,
            );
            if (bySs) {
              sourceSection = bySs.sourceSection;
              sourceStep = bySs.sourceStep;
              sourceSubstep = bySs.sourceSubstep;
            }
          }

          // Additional fallback: try to find section by title if still not found
          if (!sourceSection) {
            const fb = findSubstepByFallbackInSections(sourceAllNodes, sourceNode);
            if (fb) {
              sourceSection = fb.sourceSection;
              sourceStep = fb.sourceStep;
              sourceSubstep = fb.sourceSubstep;
            }
          }

          // Final fallback: try to find by ordinal if title-based fallback failed
          if (!sourceSection && sourceNode) {
            // Try to match by substep ordinal across all sections
            for (let i = 1; i <= 10; i++) {
              const testNode = { ...sourceNode, nodeTitle: `Substep ${i}` };
              const fb = findSubstepByFallbackInSections(sourceAllNodes, testNode);
              if (fb) {
                sourceSection = fb.sourceSection;
                sourceStep = fb.sourceStep;
                sourceSubstep = fb.sourceSubstep;
                break;
              }
            }
          }

          if (!sourceSection) {
            console.error('Source section not found for substep in handleConfirm');
            console.error('Looking for section with sectionId:', sectionMatchId);
            console.error('SubstepMatchId:', substepMatchId);
            console.error('StepMatchId:', stepMatchId);
            console.error('SourceNode:', sourceNode);
            console.error('Available sections count:', sourceAllNodes.length);
            dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          if (!sourceStep && stepMatchId) {
            sourceStep = sourceSection.steps?.find((step) =>
              stepMatchesAuditStepId(step, stepMatchId),
            );
          }
          if (!sourceStep) {
            console.error('Source step not found for substep in handleConfirm');
            console.error('Looking for step with stepId:', stepMatchId);
            dispatch(showSnackbar({ message: 'Source step not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          if (!sourceSubstep && substepMatchId) {
            sourceSubstep = sourceStep.substeps?.find((ss) =>
              substepMatchesAuditSubstepId(ss, substepMatchId),
            );
          }
          if (!sourceSubstep) {
            console.error('Source substep not found in handleConfirm');
            console.error('Looking for substep with nodeId:', substepMatchId);
            dispatch(showSnackbar({ message: 'Source substep not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        } else if (childNodeKeys.includes(sourceNode.nodeType)) {
          // Child node copy payload
          const childType = sourceNode.nodeType === 'canvas' ? 'canva' : sourceNode.nodeType;
          const childSourceNode = activeAuditTrailData
            ? enrichChildSourceNodeFromAuditRow(sourceNode, activeAuditTrailData, effectiveBreadcrumbText)
            : sourceNode;
          // Resolve source context (audit ids may not match tree — walk by child id)
          const childSectionMatchId = getChildSectionMatchId(childSourceNode);
          const childStepMatchId = getChildStepMatchId(childSourceNode);

          if (childSectionMatchId) {
            sourceSection = sourceAllNodes.find((sec) =>
              sectionMatchesAuditSectionId(sec, childSectionMatchId),
            );
          }
          if (sourceSection && childStepMatchId) {
            sourceStep = (sourceSection.steps || []).find((st) =>
              stepMatchesAuditStepId(st, childStepMatchId),
            );
          }
          if (!sourceSection && childStepMatchId) {
            const byStep = findSectionAndStepByStepId(
              sourceAllNodes,
              childStepMatchId,
            );
            if (byStep) {
              sourceSection = byStep.sourceSection;
              sourceStep = byStep.sourceStep;
            }
          }

          let sourceParent = sourceStep;
          if (sourceStep && childSourceNode.substepId) {
            const ss = (sourceStep.substeps || []).find(s => matchNodeIdConfirm(s, childSourceNode.substepId, 'tree_node_id', 'substep_id_ref', 'sub_step_tree_node_id', 'substep_tree_node_id'));
            if (ss) sourceParent = ss;
          }

          srcChild = sourceParent
            ? (sourceParent.children || []).find(c => childMatchesSourceNode(c, childSourceNode))
            : null;

          if (!srcChild) {
            outerFindChild2: for (const sec of sourceAllNodes) {
              for (const st of (sec.steps || [])) {
                const inStep = (st.children || []).find(c => childMatchesSourceNode(c, childSourceNode));
                if (inStep) {
                  sourceSection = sec;
                  sourceStep = st;
                  sourceParent = st;
                  srcChild = inStep;
                  break outerFindChild2;
                }
                for (const ss of (st.substeps || [])) {
                  const inSub = (ss.children || []).find(c => childMatchesSourceNode(c, childSourceNode));
                  if (inSub) {
                    sourceSection = sec;
                    sourceStep = st;
                    sourceParent = ss;
                    srcChild = inSub;
                    break outerFindChild2;
                  }
                }
              }
            }
          }

          if (!srcChild) {
            const byTitle = findChildInSectionsByBarTitleOrdinal(
              sourceAllNodes,
              childSourceNode,
            );
            if (byTitle) {
              sourceSection = byTitle.sourceSection;
              sourceStep = byTitle.sourceStep;
              sourceParent = byTitle.sourceParent;
              srcChild = byTitle.srcChild;
            }
          }

          if (!srcChild) {
            // Additional fallback: try to find attachment by sibling_order if ID matching failed
            if (childSourceNode?.nodeType === 'attachment') {
              // First try to get sibling_order directly from childSourceNode
              let targetOrder = null;
              if (childSourceNode?.sibling_order) {
                targetOrder = parseInt(childSourceNode.sibling_order);
              } else if (activeAuditTrailData?.version_content?.sibling_order) {
                // Try to get sibling_order from audit trail data
                targetOrder = parseInt(activeAuditTrailData.version_content.sibling_order);
              } else {
                // Fallback: Extract the number from the title (e.g., "Attachment 6" -> 6)
                const titleMatch = childSourceNode?.nodeTitle?.match(/Attachment (\d+)/i);
                targetOrder = titleMatch ? parseInt(titleMatch[1]) : null;
              }
              
              if (targetOrder) {
                for (const sec of sourceAllNodes) {
                  for (const st of (sec.steps || [])) {
                    const children = st.children || [];
                    const byOrder = children.find(c => 
                      c.type === 'attachment' && 
                      c.sibling_order === targetOrder.toString()
                    );
                    if (byOrder) {
                      sourceSection = sec;
                      sourceStep = st;
                      sourceParent = st;
                      srcChild = byOrder;
                      break;
                    }
                    
                    // Also check substeps
                    for (const ss of (st.substeps || [])) {
                      const subChildren = ss.children || [];
                      const byOrderInSub = subChildren.find(c => 
                        c.type === 'attachment' && 
                        c.sibling_order === targetOrder.toString()
                      );
                      if (byOrderInSub) {
                        sourceSection = sec;
                        sourceStep = st;
                        sourceParent = ss;
                        srcChild = byOrderInSub;
                        break;
                      }
                    }
                    if (srcChild) break;
                  }
                  if (srcChild) break;
                }
              }
            }
          }

          if (!srcChild) {
            dispatch(showSnackbar({ message: 'Source child not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          if (!sourceSection || !sourceStep) {
            console.error('Source section/step missing after resolving child in handleConfirm');
            dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }

          fromType = childType;
          fromTreeId = srcChild.tree_node_id;
          fromOrder = parseInt(srcChild.sibling_order || srcChild.procedure_tree?.sibling_order || '0', 10);
          fromSectionId = sourceSection.section_id_ref;
          fromStepId = sourceStep.step_id_ref || sourceStep.tree_node_id;
          fromSubstepId = srcChild.substep_id_ref || '';
          fromChildNodeRef = srcChild.type === "attachment" ? 
            srcChild?.detail?.attachment_tree_node_id : (srcChild.type === "canvas" || srcChild.type === "canva") ? 
            srcChild?.detail?.canvas_tree_node_id : (srcChild.type === "rich-text" || srcChild.type === "rich_text" || srcChild.type === "richText") ? 
            srcChild?.detail?.rich_text_node_id : srcChild.type === "video" ? srcChild?.detail?.video_tree_node_id : ''; 


          if (pastePosition.position === 'into' && pastePosition.targetType === 'step') {
            // Paste child into a step (append)
            const targetSection = targetAllNodes.find(sec => (sec.steps || []).some(st => st.tree_node_id === pastePosition.targetId));
            if (!targetSection) { dispatch(showSnackbar({ message: 'Target section not found.', type: 'error' })); dispatch(setLoading(false)); return; }
            const targetStep = (targetSection.steps || []).find(st => st.tree_node_id === pastePosition.targetId);
            if (!targetStep) { dispatch(showSnackbar({ message: 'Target step not found.', type: 'error' })); dispatch(setLoading(false)); return; }
            const children = targetStep.children || [];
            const sortedChildren = [...children].sort((a,b)=>{
              const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
              const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
              return aO - bO;
            });
            // Case 1: to_order = total children + 1
            toOrder = sortedChildren.length + 1;
            toProcedureTreeNodeRef = targetSection.tree_node_id;
            toSectionIdRef = targetSection.section_id_ref;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = '';
            direction = 'paste_into';
            actionType = 'paste_into';
            commentText = comment;
          } else if (pastePosition.position === 'into' && pastePosition.targetType === 'substep') {
            // Paste child into a substep (append)
            let targetSection = null; let targetStep = null; let targetSubstep = null;
            for (const sec of targetAllNodes) {
              for (const st of (sec.steps || [])) {
                const match = (st.substeps || []).find(ss => ss.tree_node_id === pastePosition.targetId);
                if (match) { targetSection = sec; targetStep = st; targetSubstep = match; break; }
              }
              if (targetSubstep) break;
            }
            if (!targetSection || !targetStep || !targetSubstep) { dispatch(showSnackbar({ message: 'Target substep not found.', type: 'error' })); dispatch(setLoading(false)); return; }
            const children = targetSubstep.children || [];
            const sortedChildren = [...children].sort((a,b)=>{
              const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
              const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
              return aO - bO;
            });
            // Case 1: to_order = total children + 1
            toOrder = sortedChildren.length + 1;
            toProcedureTreeNodeRef = targetSection.tree_node_id;
            toSectionIdRef = targetSection.section_id_ref;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = targetSubstep.substep_id_ref || targetSubstep.tree_node_id;
            direction = 'paste_into';
            actionType = 'paste_into';
            commentText = comment;
          } else if ((pastePosition.position === 'above' || pastePosition.position === 'below') && pastePosition.targetType === 'child') {
            // Paste child relative to another child
            let parent = null; let list = null; let targetChild = null;
            outer: for (const sec of targetAllNodes) {
              for (const st of (sec.steps || [])) {
                // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                const inStep = (st.children || []).find(c => {
                  const nodeId = c.tree_node_id;
                  const canvasNodeId = c.detail?.canvas_tree_node_id;
                  return nodeId === pastePosition.targetId || canvasNodeId === pastePosition.targetId;
                });
                if (inStep) { parent = { type:'step', section: sec, step: st }; list = st.children || []; targetChild = inStep; break outer; }
                for (const ss of (st.substeps || [])) {
                  // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                  const inSub = (ss.children || []).find(c => {
                    const nodeId = c.tree_node_id;
                    const canvasNodeId = c.detail?.canvas_tree_node_id;
                    return nodeId === pastePosition.targetId || canvasNodeId === pastePosition.targetId;
                  });
                  if (inSub) { parent = { type:'substep', section: sec, step: st, substep: ss }; list = ss.children || []; targetChild = inSub; break outer; }
                }
              }
            }
            if (!parent || !list || !targetChild) { dispatch(showSnackbar({ message: 'Target child not found.', type: 'error' })); dispatch(setLoading(false)); return; }
            const sortedChildren = [...list].sort((a,b)=>{
              const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
              const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
              return aO - bO;
            });
            const targetOrder = parseInt(targetChild.sibling_order || targetChild.procedure_tree?.sibling_order || '0', 10);
            // Case 2: Relative positioning
            if (pastePosition.position === 'below') {
              // Below any child: to_order = target child's sibling_order + 1
              toOrder = parseInt(targetOrder, 10) + 1;
            } else {
              // Above (paste_up)
              const parsedOrder = parseInt(targetOrder, 10);
              // If target child's sibling_order is 1, to_order = 1
              // Otherwise, to_order = sibling_order - 1
              if (parsedOrder === 1) {
                toOrder = 1;
              } else {
                toOrder = parsedOrder;
              }
            }
            toSectionIdRef = parent.section.section_id_ref;
            toStepIdRef = parent.step.step_id_ref || parent.step.tree_node_id;
            toSubstepIdRef = parent.type === 'substep' ? (parent.substep.substep_id_ref || parent.substep.tree_node_id) : '';
            toProcedureTreeNodeRef = parent.section.tree_node_id;
            // Store target child type for to_type determination
            toChildType = targetChild.type === 'canvas' ? 'canva' : targetChild.type;
            direction = pastePosition.position === 'above' ? 'paste_up' : 'paste_down';
            actionType = pastePosition.position === 'above' ? 'paste_up' : 'paste_down';
            commentText = comment;
          } else {
            dispatch(showSnackbar({ message: 'Invalid paste position for child copy.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        }

        // Calculate to_order and other target values based on node type and paste position

        if (sourceNode.nodeType === 'section') {
          // Section copy logic
          fromType = "section";
          fromTreeId = sourceSection.tree_node_id;
          fromOrder = parseInt(sourceSection.sibling_order || sourceSection.procedure_tree?.sibling_order || '0', 10);
          fromSectionId = sourceSection.section_id_ref;
          fromStepId = "";
          fromSubstepId = "";

          if (pastePosition.position === 'into') {
            // Paste section into document (at the end)
            const sortedSections = [...targetAllNodes].sort((a, b) => {
              const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
              const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
              return orderA - orderB;
            });
            
            const highestOrder = sortedSections.length > 0
              ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            
            toOrder = parseInt(highestOrder, 10) + 1;
            toProcedureTreeNodeRef = targetDocument.revisionId; // Use revisionId for document root
            toSectionIdRef = null;
            toStepIdRef = "";
            toSubstepIdRef = "";
            direction = 'paste_down';
            actionType = 'paste_down';
            commentText = `Copied ${sourceNode.nodeTitle} into ${targetDocument.type} ${targetDocument.partNumber} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          } else {
            // Paste section above or below a section
            const targetSection = targetAllNodes.find(
              section => section.tree_node_id === pastePosition.targetId
            );

            if (!targetSection) {
              dispatch(showSnackbar({ message: 'Target section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }

            const targetOrder = parseInt(targetSection.sibling_order || targetSection.procedure_tree?.sibling_order || '0', 10);
            
            if (pastePosition.position === 'above') {
              toOrder = parseInt(targetOrder, 10);
              direction = 'paste_up';
              actionType = 'paste_up';
              commentText = `Copied ${sourceNode.nodeTitle} above Section ${pastePosition.targetIndex + 1} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            } else {
              // below
              toOrder = parseInt(targetOrder, 10) + 1;
              direction = 'paste_down';
              actionType = 'paste_down';
              commentText = `Copied ${sourceNode.nodeTitle} below Section ${pastePosition.targetIndex + 1} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            }

            toProcedureTreeNodeRef = targetSection.tree_node_id;
            toSectionIdRef = targetSection.section_id_ref;
            toStepIdRef = "";
            toSubstepIdRef = "";
          }
        } else if (sourceNode.nodeType === 'step') {
          // Step copy logic
          fromType = "step";
          fromTreeId = sourceStep.tree_node_id;
          fromOrder = parseInt(sourceStep.sibling_order || sourceStep.procedure_tree?.sibling_order || '0', 10);
          fromSectionId = sourceSection.section_id_ref;
          fromStepId = sourceStep.step_id_ref || sourceStep.tree_node_id;
          fromSubstepId = "";

          if (pastePosition.position === 'into' && pastePosition.targetType === 'section') {
            // Paste step into section
            const targetSection = targetAllNodes.find(
              section => section.tree_node_id === pastePosition.targetId
            );

            if (!targetSection) {
              dispatch(showSnackbar({ message: 'Target section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }

            // Get max step order in target section
            const targetSteps = targetSection.steps || [];
            const sortedSteps = [...targetSteps].sort((a, b) => {
              const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
              const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
              return orderA - orderB;
            });

            const maxOrder = sortedSteps.length > 0
              ? parseInt(sortedSteps[sortedSteps.length - 1].sibling_order || sortedSteps[sortedSteps.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;

            toOrder = parseInt(maxOrder, 10) + 1;
            toProcedureTreeNodeRef = targetSection.tree_node_id;
            toSectionIdRef = targetSection.section_id_ref;
            toStepIdRef = ""; // Empty when pasting into section
            toSubstepIdRef = "";
            direction = 'paste_down';
            actionType = 'paste_down';
            commentText = `Copied ${sourceNode.nodeTitle} into Section ${pastePosition.targetIndex + 1} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          } else if ((pastePosition.position === 'above' || pastePosition.position === 'below') && pastePosition.targetType === 'step') {
            // Paste step above or below another step
            // Find the target step's section
            let targetStepSection = null;
            let targetStep = null;

            for (const section of targetAllNodes) {
              const steps = section.steps || [];
              const step = steps.find(s => s.tree_node_id === pastePosition.targetId);
              
              if (step) {
                targetStepSection = section;
                targetStep = step;
                break;
              }
            }

            if (!targetStepSection || !targetStep) {
              dispatch(showSnackbar({ message: 'Target step not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }

            const targetStepOrder = parseInt(targetStep.sibling_order || targetStep.procedure_tree?.sibling_order || '0', 10);

            if (pastePosition.position === 'above') {
              toOrder = parseInt(targetStepOrder, 10);
              direction = 'paste_up';
              actionType = 'paste_up';
              commentText = `Copied ${sourceNode.nodeTitle} above Step ${pastePosition.globalStepNumber} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            } else {
              // below
              toOrder = parseInt(targetStepOrder, 10) + 1;
              direction = 'paste_down';
              actionType = 'paste_down';
              commentText = `Copied ${sourceNode.nodeTitle} below Step ${pastePosition.globalStepNumber} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            }

            toProcedureTreeNodeRef = targetStepSection.tree_node_id;
            toSectionIdRef = targetStepSection.section_id_ref;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = "";
          } else {
            dispatch(showSnackbar({ message: 'Invalid paste position for step copy.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        } else if (sourceNode.nodeType === 'substep') {
          // Substep copy logic
          fromType = "substep";
          const sourceAllNodesStep = sourceStep;
          const sourceAllNodesSubstep = sourceSubstep;
          if (!sourceAllNodesStep || !sourceAllNodesSubstep) {
            dispatch(showSnackbar({ message: 'Source step or substep not found.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          fromTreeId = sourceAllNodesSubstep.tree_node_id;
          fromOrder = parseInt(sourceAllNodesSubstep.sibling_order || sourceAllNodesSubstep.procedure_tree?.sibling_order || '0', 10);
          fromSectionId = sourceSection.section_id_ref;
          fromStepId = sourceAllNodesStep.step_id_ref || sourceAllNodesStep.tree_node_id;
          fromSubstepId = sourceAllNodesSubstep.substep_id_ref || sourceAllNodesSubstep.tree_node_id;

          if (pastePosition.position === 'into' && pastePosition.targetType === 'step') {
            // Paste into a step (append at end)
            const targetSection = targetAllNodes.find(sec => sec.steps?.some(st => st.tree_node_id === pastePosition.targetId));
            if (!targetSection) {
              dispatch(showSnackbar({ message: 'Target section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }
            const targetStep = (targetSection.steps || []).find(st => st.tree_node_id === pastePosition.targetId);
            if (!targetStep) {
              dispatch(showSnackbar({ message: 'Target step not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }
            const substeps = targetStep.substeps || [];
            const sortedSubsteps = [...substeps].sort((a, b) => {
              const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
              const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
              return orderA - orderB;
            });
            const highestOrder = sortedSubsteps.length > 0
              ? parseInt(sortedSubsteps[sortedSubsteps.length - 1].sibling_order || sortedSubsteps[sortedSubsteps.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            toOrder = parseInt(highestOrder, 10) + 1;
            toSectionIdRef = targetSection.section_id_ref || targetSection.tree_node_id;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = "";
            toProcedureTreeNodeRef = targetStep.tree_node_id;
            direction = "down";
            actionType = "REVISION_NODE_COPIED";
            commentText = comment;
          } else if ((pastePosition.position === 'above' || pastePosition.position === 'below') && pastePosition.targetType === 'substep') {
            // Paste relative to a substep
            let targetSection = null;
            let targetStep = null;
            let targetSubstep = null;
            for (let i = 0; i < targetAllNodes.length; i++) {
              const section = targetAllNodes[i];
              for (let j = 0; j < (section.steps || []).length; j++) {
                const step = section.steps[j];
                const match = (step.substeps || []).find(ss => ss.tree_node_id === pastePosition.targetId);
                if (match) {
                  targetSection = section;
                  targetStep = step;
                  targetSubstep = match;
                  break;
                }
              }
              if (targetSubstep) break;
            }
            if (!targetSection || !targetStep || !targetSubstep) {
              dispatch(showSnackbar({ message: 'Target substep not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }
            // Determine order based on target substep order and direction
            const targetOrder = parseInt(targetSubstep.sibling_order || targetSubstep.procedure_tree?.sibling_order || '0', 10);
            toOrder = pastePosition.position === 'above' ? parseInt(targetOrder, 10) : parseInt(targetOrder, 10) + 1;
            toSectionIdRef = targetSection.section_id_ref || targetSection.tree_node_id;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = targetSubstep.substep_id_ref || targetSubstep.tree_node_id;
            toProcedureTreeNodeRef = targetSubstep.tree_node_id;
            direction = pastePosition.position === 'above' ? 'up' : 'down';
            actionType = "REVISION_NODE_COPIED";
            commentText = comment;
          }
        }


        // Generate node_indexing for copy operations and prepare new content
        let nodeIndexing = null;
        let newContentForVersion = null;
        let actionToShow = '';
        
        if (sourceNode.nodeType === 'section') {
          // Prepare new content after copy
          const newContent = prepareCopyNewContent({
            targetAllNodes: targetAllNodes,
            sourceSection: sourceSection,
            targetDoc: targetDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
            toSectionIdRef: toSectionIdRef,
          });

          // Store newContent for version_content
          newContentForVersion = newContent;

          // Generate node_indexing
          nodeIndexing = buildNodeIndexingForSectionCopy({
            sourceSection,
            sourceAllNodes,
            newContent,
            targetAllNodes,
            targetDoc: targetDocument,
            sourceDoc: sourceDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
          });

          // Build action_to_show for section copy
          const sourceIndexMap = generateNodeIndexing(sourceAllNodes);
          const targetIndexMap = generateNodeIndexing(targetAllNodes);
          
          const sourceSectionName = sourceIndexMap[sourceSection.tree_node_id] || 
            sourceIndexMap[sourceSection.section_id_ref] || 
            `Section ${(sourceAllNodes.findIndex(s => s.tree_node_id === sourceSection.tree_node_id) + 1)}`;
          
          const sourceDocInfo = `from ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}`;
          const targetDocInfo = `into ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          
          if (pastePosition.position === 'into') {
            actionToShow = `User Copied ${sourceSectionName} ${sourceDocInfo} ${targetDocInfo}`;
          } else {
            // For above/below, find target section name
            // Use pastePosition.targetIndex if available (the index selected by user)
            // Otherwise fall back to finding it from the structure
            let targetSectionName = 'the target section';
            if (pastePosition.targetIndex !== null && pastePosition.targetIndex !== undefined) {
              // Use the index that was selected by the user (0-based, so add 1)
              targetSectionName = `Section ${pastePosition.targetIndex + 1}`;
            } else {
              // Fallback: find target section from structure
              const targetSection = targetAllNodes.find(
                section => section.tree_node_id === pastePosition.targetId
              );
              if (targetSection) {
                targetSectionName = targetIndexMap[targetSection.tree_node_id] || 
                  targetIndexMap[targetSection.section_id_ref] || 
                  `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`;
              }
            }
            
            const positionWord = pastePosition.position === 'above' ? 'above' : 'below';
            actionToShow = `User Copied ${sourceSectionName} ${sourceDocInfo} ${positionWord} ${targetSectionName} ${targetDocInfo}`;
          }
        } else if (sourceNode.nodeType === 'step') {
          // Prepare new content after step copy
          const newContent = prepareStepMoveNewContent({
            targetAllNodes: targetAllNodes,
            sourceStep: sourceStep,
            moveDocument: sourceDocument,
            targetDoc: targetDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
            toSectionIdRef: toSectionIdRef,
            moveCreateCopy: true, // This is a copy operation
          });

          // Store newContent for version_content
          newContentForVersion = newContent;

          // Generate node_indexing for step copy
          nodeIndexing = buildNodeIndexingForStepCopy({
            sourceStep,
            sourceAllNodes,
            newContent,
            targetAllNodes,
            targetDoc: targetDocument,
            sourceDoc: sourceDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
          });

       
          actionToShow = Object.values(nodeIndexing)[0]?.title?.split("System Performs Operation")[0].trim();
        } else if (sourceNode.nodeType === 'substep') {
          // Prepare new content after substep copy
          const newContent = prepareSubstepMoveNewContent({
            targetAllNodes: targetAllNodes,
            sourceSubstep: sourceSubstep,
            moveDocument: sourceDocument,
            targetDoc: targetDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
            toStepIdRef: toStepIdRef,
            moveCreateCopy: true, // This is a copy operation
          });

          // Store newContent for version_content
          newContentForVersion = newContent;

          // Generate node_indexing for substep copy
          nodeIndexing = buildNodeIndexingForSubstepCopy({
            sourceSubstep,
            sourceAllNodes,
            newContent,
            targetDoc: targetDocument,
            sourceDoc: sourceDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
            targetAllNodesOriginal: targetAllNodes,
          });

          // Build action_to_show for substep copy
          const sourceIndexMap = generateNodeIndexing(sourceAllNodes);
          const targetIndexMap = generateNodeIndexing(targetAllNodes);
          
          // Find source section and source step that contains the source substep
          let sourceSection = null;
          let sourceStep = null;
          for (const section of sourceAllNodes) {
            for (const step of section.steps || []) {
              const substep = (step.substeps || []).find(
                s => s.tree_node_id === sourceSubstep.tree_node_id || s.substep_id_ref === sourceSubstep.substep_id_ref
              );
              if (substep) {
                sourceSection = section;
                sourceStep = step;
                break;
              }
            }
            if (sourceStep) break;
          }
          
          const sourceSectionName = sourceSection 
            ? (sourceIndexMap[sourceSection.tree_node_id] || 
               sourceIndexMap[sourceSection.section_id_ref] || 
               `Section ${(sourceAllNodes.findIndex(s => s.tree_node_id === sourceSection.tree_node_id) + 1)}`)
            : 'the source section';
          
          const sourceStepName = sourceStep 
            ? (sourceIndexMap[sourceStep.tree_node_id] || 
               sourceIndexMap[sourceStep.step_id_ref] || 
               `Step ${(sourceAllNodes.reduce((acc, section) => {
                 const stepIndex = (section.steps || []).findIndex(s => s.tree_node_id === sourceStep.tree_node_id || s.step_id_ref === sourceStep.step_id_ref);
                 if (stepIndex !== -1) {
                   const stepsBefore = sourceAllNodes.slice(0, sourceAllNodes.indexOf(section))
                     .reduce((sum, s) => sum + (s.steps || []).length, 0);
                   return stepsBefore + stepIndex + 1;
                 }
                 return acc;
               }, 0))}`)
            : 'the source step';
          
          const sourceSubstepName = sourceIndexMap[sourceSubstep.tree_node_id] || 
            sourceIndexMap[sourceSubstep.substep_id_ref] || 
            `Substep in ${sourceStepName}`;
          
          const sourceDocInfo = `in ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}`;
          const targetDocInfo = `${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          
          if (pastePosition.position === 'into') {
            // Find target step name
            let targetStep = null;
            let targetSection = null;
            for (const section of targetAllNodes) {
              targetStep = (section.steps || []).find(
                step => step.tree_node_id === pastePosition.targetId || step.step_id_ref === pastePosition.targetId
              );
              if (targetStep) {
                targetSection = section;
                break;
              }
            }
            const targetStepName = targetStep 
              ? (targetIndexMap[targetStep.tree_node_id] || 
                 targetIndexMap[targetStep.step_id_ref] || 
                 `Step ${pastePosition.globalStepNumber || ''}`)
              : 'the target step';
            
            const targetSectionName = targetSection 
              ? (targetIndexMap[targetSection.tree_node_id] || 
                 targetIndexMap[targetSection.section_id_ref] || 
                 `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`)
              : 'the target section';
            
            actionToShow = `User Copied ${sourceSubstepName} from ${sourceStepName} of ${sourceSectionName} ${sourceDocInfo}, into ${targetStepName} of ${targetSectionName} in ${targetDocInfo}`;
          } else {
            // For above/below, find target substep name
            let targetSubstep = null;
            let targetStep = null;
            let targetSection = null;
            for (const section of targetAllNodes) {
              for (const step of section.steps || []) {
                targetSubstep = (step.substeps || []).find(
                  substep => substep.tree_node_id === pastePosition.targetId || substep.substep_id_ref === pastePosition.targetId
                );
                if (targetSubstep) {
                  targetStep = step;
                  targetSection = section;
                  break;
                }
              }
              if (targetSubstep) break;
            }
            
            const targetSubstepName = targetSubstep 
              ? (targetIndexMap[targetSubstep.tree_node_id] || 
                 targetIndexMap[targetSubstep.substep_id_ref] || 
                 `Substep in ${targetIndexMap[targetStep?.tree_node_id] || targetIndexMap[targetStep?.step_id_ref] || 'Step'}`)
              : 'the target substep';
            
            const targetStepName = targetStep 
              ? (targetIndexMap[targetStep.tree_node_id] || 
                 targetIndexMap[targetStep.step_id_ref] || 
                 `Step ${pastePosition.globalStepNumber || ''}`)
              : 'the target step';
            
            const targetSectionName = targetSection 
              ? (targetIndexMap[targetSection.tree_node_id] || 
                 targetIndexMap[targetSection.section_id_ref] || 
                 `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`)
              : 'the target section';
            
            const positionWord = pastePosition.position === 'above' ? 'above' : 'below';
            actionToShow = `User Copied ${sourceSubstepName} from ${sourceStepName} of ${sourceSectionName} ${sourceDocInfo}, ${positionWord} ${targetSubstepName} in ${targetStepName} of ${targetSectionName} in ${targetDocInfo}`;
          }
        } else if ([ 'attachment','video','rich-text','form','canva','canvas' ].includes(sourceNode.nodeType)) {
          // For child nodes, build proper action_to_show
          const childType = sourceNode.nodeType === 'canvas' ? 'canva' : sourceNode.nodeType;
          const childTypeLabel = formatChildTypeLabel(childType);
          const childSourceNodeForAction = activeAuditTrailData
            ? enrichChildSourceNodeFromAuditRow(sourceNode, activeAuditTrailData, effectiveBreadcrumbText)
            : sourceNode;
          
          // Re-find source child node and parent (they were defined in the earlier block)
          srcChild = null;
          let sourceParent = null;
          const cSec = getChildSectionMatchId(childSourceNodeForAction);
          const cSt = getChildStepMatchId(childSourceNodeForAction);
          let sourceSectionForChild = cSec
            ? sourceAllNodes.find((sec) =>
                matchNodeIdConfirm(sec, cSec, 'tree_node_id', 'section_id_ref', 'section_tree_node_id'),
              )
            : null;
          let sourceStepForChild =
            sourceSectionForChild && cSt
              ? (sourceSectionForChild.steps || []).find((st) =>
                  matchNodeIdConfirm(st, cSt, 'tree_node_id', 'step_id_ref', 'step_tree_node_id'),
                )
              : null;
          if (!sourceSectionForChild && cSt) {
            const byStep = findSectionAndStepByStepId(sourceAllNodes, cSt);
            if (byStep) {
              sourceSectionForChild = byStep.sourceSection;
              sourceStepForChild = byStep.sourceStep;
            }
          }

          if (sourceStepForChild) {
            sourceParent = sourceStepForChild;
            if (childSourceNodeForAction.substepId) {
              const ss = (sourceStepForChild.substeps || []).find(s =>
                matchNodeIdConfirm(s, childSourceNodeForAction.substepId, 'tree_node_id', 'substep_id_ref', 'sub_step_tree_node_id', 'substep_tree_node_id')
              );
              if (ss) sourceParent = ss;
            }

            srcChild = (sourceParent.children || []).find(c => childMatchesSourceNode(c, childSourceNodeForAction));
          }

          if (!srcChild) {
            outerFindChild3: for (const sec of sourceAllNodes) {
              for (const st of (sec.steps || [])) {
                const inStep = (st.children || []).find(c => childMatchesSourceNode(c, childSourceNodeForAction));
                if (inStep) {
                  sourceParent = st;
                  sourceStepForChild = st;
                  sourceSectionForChild = sec;
                  srcChild = inStep;
                  break outerFindChild3;
                }
                for (const ss of (st.substeps || [])) {
                  const inSub = (ss.children || []).find(c => childMatchesSourceNode(c, childSourceNodeForAction));
                  if (inSub) {
                    sourceParent = ss;
                    sourceStepForChild = st;
                    sourceSectionForChild = sec;
                    srcChild = inSub;
                    break outerFindChild3;
                  }
                }
              }
            }
          }

          if (!srcChild) {
            const byTitle = findChildInSectionsByBarTitleOrdinal(
              sourceAllNodes,
              childSourceNodeForAction,
            );
            if (byTitle) {
              sourceSectionForChild = byTitle.sourceSection;
              sourceStepForChild = byTitle.sourceStep;
              sourceParent = byTitle.sourceParent;
              srcChild = byTitle.srcChild;
            }
          }
          
          if (!srcChild) {
            // Fallback action_to_show if child not found
            actionToShow = `User Copied ${childTypeLabel} from ${sourceDocument.partNumber}, Rev ${sourceDocument.revision} into ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            newContentForVersion = targetAllNodes;
          } else {
            // Get source child node display number
            const sourceChildNumber = getChildNodeDisplayNumber(srcChild, childType, sourceAllNodes);
            
            // Get source section and step names
            const sourceIndexMap = generateNodeIndexing(sourceAllNodes);
            const sourceSectionName = sourceSectionForChild 
              ? (sourceIndexMap[sourceSectionForChild.tree_node_id] || 
                 sourceIndexMap[sourceSectionForChild.section_id_ref] || 
                 `Section ${(sourceAllNodes.findIndex(s => s.tree_node_id === sourceSectionForChild.tree_node_id) + 1)}`)
              : 'the source section';
            
            const sourceStepName = sourceStepForChild 
              ? (sourceIndexMap[sourceStepForChild.tree_node_id] || 
                 sourceIndexMap[sourceStepForChild.step_id_ref] || 
                 `Step ${(sourceAllNodes.reduce((acc, section) => {
                   const stepIndex = (section.steps || []).findIndex(s => s.tree_node_id === sourceStepForChild.tree_node_id || s.step_id_ref === sourceStepForChild.step_id_ref);
                   if (stepIndex !== -1) {
                     const stepsBefore = sourceAllNodes.slice(0, sourceAllNodes.indexOf(section))
                       .reduce((sum, s) => sum + (s.steps || []).length, 0);
                     return stepsBefore + stepIndex + 1;
                   }
                   return acc;
                 }, 0))}`)
              : 'the source step';
            
            // Check if source child is in a substep
            let sourceSubstepName = '';
            if (sourceNode.substepId && sourceParent !== sourceStepForChild && sourceParent.substep_id_ref) {
              const sourceSubstep = sourceParent;
              sourceSubstepName = sourceIndexMap[sourceSubstep.tree_node_id] || 
                sourceIndexMap[sourceSubstep.substep_id_ref] || 
                `Substep in ${sourceStepName}`;
            }
          
          const sourceDocInfo = `from ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}`;
          const targetDocInfo = `in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          
          // Get target information
          const targetIndexMap = generateNodeIndexing(targetAllNodes);
          
          if (pastePosition.position === 'into' && pastePosition.targetType === 'step') {
            // Copy into a step
            const targetSection = targetAllNodes.find(sec => (sec.steps || []).some(st => st.tree_node_id === pastePosition.targetId));
            const targetStep = targetSection ? (targetSection.steps || []).find(st => st.tree_node_id === pastePosition.targetId) : null;
            
            const targetSectionName = targetSection 
              ? (targetIndexMap[targetSection.tree_node_id] || 
                 targetIndexMap[targetSection.section_id_ref] || 
                 `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`)
              : 'the target section';
            
            const targetStepName = targetStep 
              ? (targetIndexMap[targetStep.tree_node_id] || 
                 targetIndexMap[targetStep.step_id_ref] || 
                 `Step ${pastePosition.globalStepNumber || ''}`)
              : 'the target step';
            
            const sourceContext = sourceSubstepName 
              ? `from ${sourceSubstepName} in ${sourceStepName} in ${sourceSectionName}`
              : `from ${sourceStepName} in ${sourceSectionName}`;
            
            actionToShow = `User Copied ${childTypeLabel} ${sourceChildNumber} ${sourceContext} ${sourceDocInfo} into ${targetStepName} in ${targetSectionName} ${targetDocInfo}`;
          } else if (pastePosition.position === 'into' && pastePosition.targetType === 'substep') {
            // Copy into a substep
            let targetSection = null;
            let targetStep = null;
            let targetSubstep = null;
            for (const section of targetAllNodes) {
              for (const step of section.steps || []) {
                targetSubstep = (step.substeps || []).find(ss => ss.tree_node_id === pastePosition.targetId);
                if (targetSubstep) {
                  targetSection = section;
                  targetStep = step;
                  break;
                }
              }
              if (targetSubstep) break;
            }
            
            const targetSectionName = targetSection 
              ? (targetIndexMap[targetSection.tree_node_id] || 
                 targetIndexMap[targetSection.section_id_ref] || 
                 `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`)
              : 'the target section';
            
            const targetStepName = targetStep 
              ? (targetIndexMap[targetStep.tree_node_id] || 
                 targetIndexMap[targetStep.step_id_ref] || 
                 `Step ${pastePosition.globalStepNumber || ''}`)
              : 'the target step';
            
            const targetSubstepName = targetSubstep 
              ? (targetIndexMap[targetSubstep.tree_node_id] || 
                 targetIndexMap[targetSubstep.substep_id_ref] || 
                 `Substep in ${targetStepName}`)
              : 'the target substep';
            
            const sourceContext = sourceSubstepName 
              ? `from ${sourceSubstepName} in ${sourceStepName} in ${sourceSectionName}`
              : `from ${sourceStepName} in ${sourceSectionName}`;
            
            actionToShow = `User Copied ${childTypeLabel} ${sourceChildNumber} ${sourceContext} ${sourceDocInfo} into ${targetSubstepName} in ${targetStepName} in ${targetSectionName} ${targetDocInfo}`;
          } else if ((pastePosition.position === 'above' || pastePosition.position === 'below') && pastePosition.targetType === 'child') {
            // Copy above/below another child
            let parent = null;
            let targetChild = null;
            outer: for (const section of targetAllNodes) {
              for (const step of section.steps || []) {
                const inStep = (step.children || []).find(c => {
                  const nodeId = c.tree_node_id;
                  const canvasNodeId = c.detail?.canvas_tree_node_id;
                  return nodeId === pastePosition.targetId || canvasNodeId === pastePosition.targetId;
                });
                if (inStep) {
                  parent = { type: 'step', section, step };
                  targetChild = inStep;
                  break outer;
                }
                for (const substep of step.substeps || []) {
                  const inSub = (substep.children || []).find(c => {
                    const nodeId = c.tree_node_id;
                    const canvasNodeId = c.detail?.canvas_tree_node_id;
                    return nodeId === pastePosition.targetId || canvasNodeId === pastePosition.targetId;
                  });
                  if (inSub) {
                    parent = { type: 'substep', section, step, substep };
                    targetChild = inSub;
                    break outer;
                  }
                }
              }
              if (targetChild) break;
            }
            
            if (parent && targetChild) {
              const targetSectionName = parent.section 
                ? (targetIndexMap[parent.section.tree_node_id] || 
                   targetIndexMap[parent.section.section_id_ref] || 
                   `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === parent.section.tree_node_id) + 1)}`)
                : 'the target section';
              
              const targetStepName = parent.step 
                ? (targetIndexMap[parent.step.tree_node_id] || 
                   targetIndexMap[parent.step.step_id_ref] || 
                   `Step ${pastePosition.globalStepNumber || ''}`)
                : 'the target step';
              
              let targetSubstepName = '';
              if (parent.type === 'substep' && parent.substep) {
                targetSubstepName = targetIndexMap[parent.substep.tree_node_id] || 
                  targetIndexMap[parent.substep.substep_id_ref] || 
                  `Substep in ${targetStepName}`;
              }
              
              const targetChildTypeLabel = formatChildTypeLabel(targetChild.type === 'canvas' ? 'canva' : targetChild.type);
              const targetChildNumber = getChildNodeDisplayNumber(targetChild, targetChild.type === 'canvas' ? 'canva' : targetChild.type, targetAllNodes);
              
              const sourceContext = sourceSubstepName 
                ? `from ${sourceSubstepName} in ${sourceStepName} in ${sourceSectionName}`
                : `from ${sourceStepName} in ${sourceSectionName}`;
              
              const positionWord = pastePosition.position === 'above' ? 'above' : 'below';
              const targetContext = targetSubstepName 
                ? `${positionWord} ${targetChildTypeLabel} ${targetChildNumber} in ${targetSubstepName} in ${targetStepName} in ${targetSectionName}`
                : `${positionWord} ${targetChildTypeLabel} ${targetChildNumber} in ${targetStepName} in ${targetSectionName}`;
              
              actionToShow = `User Copied ${childTypeLabel} ${sourceChildNumber} ${sourceContext} ${sourceDocInfo} ${targetContext} ${targetDocInfo}`;
            } else {
              // Fallback if target child not found
              actionToShow = `User Copied ${childTypeLabel} ${sourceChildNumber} from ${sourceStepName} in ${sourceSectionName} ${sourceDocInfo} ${targetDocInfo}`;
            }
          }
          
          // When copying a child node, ensure the previewed structure (with the inserted child)
          // is captured in the audit trail newContent. Fall back to the existing target data if
          // for some reason the preview data (newData) is unavailable.
          const childNewContent = Array.isArray(newData) && newData.length > 0
            ? newData
            : targetAllNodes;
          newContentForVersion = childNewContent;
          }
        } else {
          // For other node types, use default action_to_show
          newContentForVersion = targetAllNodes;
          actionToShow = `User Copied ${sourceDocument.type} ${sourceDocument.partNumber} Rev ${sourceDocument.revision} into ${targetDocument.type} ${targetDocument.revision} ${targetDocument.partNumber}`;
        }

        // Build version_content with node_indexing if available
        const versionContent = {
          action_to_show: actionToShow,
          originingalContent: extractVersionStructure(targetAllNodes),
          newContent: newContentForVersion ? extractVersionStructure(newContentForVersion) : [], 
            rev: targetDocument.revision,
            status: targetDocument.status || 'Not Submitted',
            item_code_type: targetDocument.type,
            partNumber: targetDocument.partNumber,
            from_part_ref: sourceDocument.partNumber,
            from_part_rev: sourceDocument.revision,
          to_part_ref: targetDocument.partNumber,
          to_part_rev: targetDocument.revision,
          partDescription: targetDocument.description || '',
        };


        // Build audit trail data in old format
        const auditData = await getAuditTrailData({
          action: `${fromType.toUpperCase()}_COPIED`,
          comment: comment || commentText,
          version_content: versionContent,
        },false);

        // Build payload in old format
        const payload = {
          from_procedure_tree_node_ref: fromTreeId,
          from_order: fromOrder,
          from_type: fromType,
          from_part_id_ref: sourceDocument.partId,
          from_procedure_rev_ref: sourceDocument.revisionId,
          from_section_id_ref: fromSectionId,
          from_step_id_ref: fromStepId,
          from_substep_id_ref: fromSubstepId,
          ...(fromChildNodeRef ? { from_child_node_ref: fromChildNodeRef } : {}),
          to_procedure_tree_node_ref: toProcedureTreeNodeRef,
          to_order: parseInt(toOrder || 0, 10),
          to_type: (() => {
            const leafTypes = [
              'attachment',
              'video',
              'rich-text',
              'rich_text',
              'richText',
              'form',
              'canva',
              'canvas',
            ];
            // Created node is always the *source* leaf type. Target sibling only affects order.
            if (leafTypes.includes(sourceNode.nodeType)) {
              if (
                sourceNode.nodeType === 'canvas' ||
                sourceNode.nodeType === 'canva'
              ) {
                return 'canvas';
              }
              if (
                sourceNode.nodeType === 'rich-text' ||
                sourceNode.nodeType === 'rich_text' ||
                sourceNode.nodeType === 'richText'
              ) {
                return 'rich-text';
              }
              return sourceNode.nodeType;
            }
            if (toChildType) {
              return normalizeVersionNodeCopyCreateType(toChildType);
            }
            if (sourceNode.nodeType === 'section') return 'section';
            if (sourceNode.nodeType === 'step') return 'step';
            if (sourceNode.nodeType === 'substep') return 'substep';
            return 'substep';
          })(),
          to_part_id_ref: targetDocument.partId,
          to_procedure_rev_ref: targetDocument.revisionId,
          to_section_id_ref: toSectionIdRef || "",
          to_step_id_ref: toStepIdRef || "",
          to_substep_id_ref: toSubstepIdRef || "",
          direction: direction==="up" ? "paste_up" : direction==="down" ? "paste_down" : direction,
          audit_trail_data: auditData,
        };

        if (isVersionHistory) {
          payload.procedure_node_data = resolveProcedureNodeDataForVersionHistoryCopy({
            auditTrailRow: activeAuditTrailData,
            sourceSubtreeRoot:
              sourceNode.nodeType === 'section'
                ? sourceSection
                : sourceNode.nodeType === 'step'
                  ? sourceStep
                  : sourceNode.nodeType === 'substep'
                    ? sourceSubstep
                    : null,
            sourceNodeType: sourceNode.nodeType,
            sourceChildNode: childNodeKeys.includes(sourceNode.nodeType)
              ? srcChild
              : null,
          });
        }

        const snapshotBaseData = Array.isArray(newContentForVersion) && newContentForVersion.length > 0
          ? newContentForVersion
          : targetAllNodes;

        const current_snapshot = await uploadJsonToS3(
          prepareCurrentSnapshot({
            allNodesData: snapshotBaseData,
            part_number: targetDocument.partNumber,
            procedure_rev: targetDocument.revision,
            procedure_status: targetDocument.status,
            item_code_type: targetDocument.type,
            partDescription: targetDocument.description || "",
            part_ref: targetDocument.partId,
            procedure_rev_ref: targetDocument.revisionId
          }),
          targetDocument.partNumber,
          targetDocument.revision
        );

        const current_updated_snapshot = await uploadJsonToS3(
          prepareCurrentSnapshot({
            allNodesData: snapshotBaseData,
            part_number: targetDocument.partNumber,
            procedure_rev: targetDocument.revision,
            procedure_status: targetDocument.status,
            item_code_type: targetDocument.type,
            partDescription: targetDocument.description || "",
            part_ref: targetDocument.partId,
            procedure_rev_ref: targetDocument.revisionId
          }),
          targetDocument.partNumber,
          targetDocument.revision + "_current_updated_snapshot",true
        );

        payload.current_snapshot = current_snapshot;
        payload.current_updated_snapshot = current_updated_snapshot;

        // Add node_indexing to the main payload if available
        if(childNodeKeys.includes(sourceNode.nodeType)) {
          const previous_content_snapshot = await uploadJsonToS3(
            prepareCurrentSnapshot({
              allNodesData: targetAllNodes,
              part_number: targetDocument.partNumber,
              procedure_rev: targetDocument.revision,
              procedure_status: targetDocument.status,
              item_code_type: targetDocument.type,
              partDescription: targetDocument.description || "",
              part_ref: targetDocument.partId,
              procedure_rev_ref: targetDocument.revisionId
            }),
            targetDocument.partNumber,
            targetDocument.revision + `${sourceNode.nodeType.toUpperCase()}_COPIED_previous`,
            true
          );

          const revAndTrainingData = await getRevAndTraining(
            dispatch,
            payload.from_part_id_ref,
          );

          const new_content_snapshot = await uploadJsonToS3(
            {
              data: newContentForVersion || [],
              ...payload.audit_trail_data.version_content,
              main_entry: undefined,
              operation: "single_audit_trail",
              tree_node_id: fromTreeId,
              revision: payload.audit_trail_data.version_content.rev,
              ...revAndTrainingData,
            },
            targetDocument.partNumber,
            targetDocument.revision + `${sourceNode.nodeType.toUpperCase()}_COPIED_new`,
            true
          );

          payload.audit_trail_data.version_content = {
            action_to_show: payload.audit_trail_data.version_content.action_to_show,
          };

          payload.node_indexing = {
            [fromTreeId]: {
              title: actionToShow,
              previous_content_snapshot,
              new_content_snapshot,
            }
          };

          payload.audit_trail_data.previous_content_snapshot = previous_content_snapshot;
          payload.audit_trail_data.new_content_snapshot = new_content_snapshot;
        }else if (nodeIndexing && Object.keys(nodeIndexing).length > 0) {
          payload.node_indexing = {};

          const revAndTrainingDataBulk = await getRevAndTraining(
            dispatch,
            payload.from_part_id_ref,
          );

          const snapshot_url = await uploadJsonToS3(
            {
              ...nodeIndexing,
              version_content: {
                ...payload.audit_trail_data.version_content,
                main_entry: undefined,
                revision: payload.audit_trail_data.version_content.rev,
                operation: "bulk_audit_trail",
                ...revAndTrainingDataBulk,
              }
            },
            targetDocument.partNumber,
            targetDocument.revision + `${sourceNode.nodeType.toUpperCase()}_COPIED`,
            true
          );

          payload.audit_trail_data.version_content = {
            action_to_show: payload.audit_trail_data.version_content.action_to_show,
            main_entry: true
          };

          payload.audit_trail_data.previous_content_snapshot = snapshot_url;
          payload.audit_trail_data.new_content_snapshot = snapshot_url;

          Object.entries(nodeIndexing).forEach(([key, value]) => {
            payload.node_indexing[key] = {
              title: value.title,
              previous_content_snapshot: snapshot_url,
              new_content_snapshot: snapshot_url,
            };
          });
        }

        // Clean up empty or null values (matching old implementation pattern)
        // Note: Empty strings for from_step_id_ref, from_substep_id_ref, to_step_id_ref, to_substep_id_ref are kept
        // Only remove null or empty to_section_id_ref for "into" case
        Object.keys(payload).forEach((key) => {
          if (payload[key] === "null" || payload[key] === null) {
            // Remove null values
            delete payload[key];
          } else if (payload[key] === "") {
            // For "into" case, remove empty to_section_id_ref
            // Keep empty strings for step/substep refs (as per old implementation)
            if (key === 'to_section_id_ref' && pastePosition.position === 'into') {
              delete payload[key];
            }
            // Otherwise keep empty strings (for step/substep refs)
          }
        });

        // Determine which API to use based on whether the node has children
        let useSimpleNodeCopyAPI = false;
        let sourceNodeData = null;
        
        if (childNodeKeys.includes(sourceNode.nodeType)) {
          // Child nodes always use the simple API
          useSimpleNodeCopyAPI = true;
        } else if (sourceNode.nodeType === 'section') {
          const hasSteps =
            sourceSection && sourceSection.steps && sourceSection.steps.length > 0;
          const hasSectionChildren =
            sourceSection &&
            sourceSection.children &&
            sourceSection.children.length > 0;
          const auditProcLen = Array.isArray(payload.procedure_node_data)
            ? payload.procedure_node_data.length
            : 0;
          const auditSubtree =
            isVersionHistory && auditProcLen > 1;
          useSimpleNodeCopyAPI =
            !hasSteps && !hasSectionChildren && !auditSubtree;
          sourceNodeData = sourceSection;
        } else if (sourceNode.nodeType === 'step') {
          // Check if step has substeps or child nodes
          const hasSubsteps = sourceStep && sourceStep.substeps && sourceStep.substeps.length > 0;
          const hasChildren = sourceStep && sourceStep.children && sourceStep.children.length > 0;
          useSimpleNodeCopyAPI = !hasSubsteps && !hasChildren;
          sourceNodeData = sourceStep;
        } else if (sourceNode.nodeType === 'substep') {
          // Check if substep has child nodes
          const hasChildren = sourceSubstep && sourceSubstep.children && sourceSubstep.children.length > 0;
          useSimpleNodeCopyAPI = !hasChildren;
          sourceNodeData = sourceSubstep;
        }
        
        // Make API call based on the determination
        try {
          let copyResponse;
          if (useSimpleNodeCopyAPI) {
            // Use the simple node copy API (version-node-copy/create)
            // Build payload for simple node copy API (match CopyReplace: never send "" for UUID columns)
            const simplePayload = {
              type: normalizeVersionNodeCopyCreateType(payload.to_type),
              // Match AddEditSection createSection: sibling_order is numeric for section/step/substep
              sibling_order:
                sourceNode.nodeType === 'section' ||
                sourceNode.nodeType === 'step' ||
                sourceNode.nodeType === 'substep'
                  ? parseInt(String(payload.to_order ?? 0), 10)
                  : String(payload.to_order ?? ''),
              revision_id_ref: targetDocument.revisionId,
              audit_trail_data: payload.audit_trail_data,
            };
            if (payload.to_section_id_ref) {
              simplePayload.section_id_ref = payload.to_section_id_ref;
            }
            if (payload.to_step_id_ref) {
              simplePayload.step_id_ref = payload.to_step_id_ref;
            }
            if (payload.to_substep_id_ref) {
              simplePayload.substep_id_ref = payload.to_substep_id_ref;
            }

            // Add the source node's data to the payload
            if (sourceNodeData) {
              // For section, step, substep - copy their detail fields
              if (sourceNode.nodeType === 'section') {
                const secDetail =
                  sourceNodeData.detail && typeof sourceNodeData.detail === 'object'
                    ? sourceNodeData.detail
                    : {};
                const sectionName =
                  sourceNodeData.section_name ?? secDetail.section_name;
                if (sectionName) {
                  simplePayload.section_name = sectionName;
                }
                simplePayload.short_description =
                  sourceNodeData.short_description ??
                  secDetail.short_description ??
                  '';
                // Always target procedure part (mirrors createSection sectionData.part_required_id_ref)
                simplePayload.part_required_id_ref = targetDocument.partId;
                simplePayload.is_hide_heading_and_desc =
                  sourceNodeData.is_hide_heading_and_desc ??
                  secDetail.is_hide_heading_and_desc ??
                  false;
              } else if (sourceNode.nodeType === 'step') {
                // Copy step data
                if (sourceNodeData.step_data) {
                  simplePayload.step_data = sourceNodeData.step_data;
                }
                simplePayload.short_description = sourceNodeData.short_description || "";
                simplePayload.is_hide_step_heading = sourceNodeData.is_hide_step_heading || false;
                simplePayload.is_hide_step_details = sourceNodeData.is_hide_step_details || false;
                simplePayload.is_hide_bom_table = sourceNodeData.is_hide_bom_table || false;
                if (sourceNodeData.bom_table_for_step_title_ref) {
                  simplePayload.bom_table_for_step_title_ref =
                    sourceNodeData.bom_table_for_step_title_ref;
                }
                if (sourceNodeData.part_number_column_title_ref) {
                  simplePayload.part_number_column_title_ref =
                    sourceNodeData.part_number_column_title_ref;
                }
              } else if (sourceNode.nodeType === 'substep') {
                // Mirror createSubstep (AddEditSubstep): content lives on `detail`; API expects
                // short_description, revision/section/step/sibling_order, and part_required_id_ref.
                const skipDetailKeys = new Set([
                  'type',
                  'section_id_ref',
                  'step_id_ref',
                  'revision_id_ref',
                  'substep_id_ref',
                  'sibling_order',
                  'part_required_id_ref',
                  'tree_node_id',
                  'procedure_tree_ref',
                  'procedure_tree_id',
                  'created_at',
                  'updated_at',
                ]);
                const det =
                  sourceNodeData.detail && typeof sourceNodeData.detail === 'object'
                    ? sourceNodeData.detail
                    : {};
                Object.keys(det).forEach((key) => {
                  if (skipDetailKeys.has(key)) return;
                  const value = det[key];
                  if (value !== '' && value !== null && value !== undefined) {
                    simplePayload[key] = value;
                  }
                });
                simplePayload.short_description =
                  det.short_description ?? sourceNodeData.short_description ?? '';
                const desc = det.description ?? sourceNodeData.description;
                if (desc !== undefined && desc !== null && desc !== '') {
                  simplePayload.description = desc;
                }
                simplePayload.part_required_id_ref = targetDocument.partId;
              }
            } else if (childNodeKeys.includes(sourceNode.nodeType)) {
              const isCanvas =
                sourceNode.nodeType === 'canvas' ||
                sourceNode.nodeType === 'canva';

              if (isCanvas) {
                // Match create-canvas POST shape + `type: "canvas"` for version-node-copy/create.
                if (
                  !srcChild ||
                  (srcChild.type !== 'canva' && srcChild.type !== 'canvas')
                ) {
                  dispatch(
                    showSnackbar({
                      message: 'Source canvas not found for copy.',
                      type: 'error',
                    }),
                  );
                  dispatch(setLoading(false));
                  return;
                }
                const pages = await resolveCanvasPagesForVersionCopy(srcChild);
                if (!Array.isArray(pages) || pages.length === 0) {
                  dispatch(
                    showSnackbar({
                      message:
                        'Canvas pages are missing. Need detail.pages or a reachable canvas_json_data_file.',
                      type: 'error',
                    }),
                  );
                  dispatch(setLoading(false));
                  return;
                }
                const det = srcChild.detail || {};
                simplePayload.type = 'canvas';
                simplePayload.name = det.name ?? '';
                simplePayload.short_description = det.short_description ?? '';
                simplePayload.pages = pages;
                simplePayload.part_required_id_ref = targetDocument.partId;

                try {
                  simplePayload.canvas_json_data_file =
                    await uploadCanvasPagesJsonForVersionCopy(pages);
                } catch (uploadErr) {
                  console.error(
                    '[VersionHistoryCopy] Canvas pages JSON upload failed:',
                    uploadErr,
                  );
                  dispatch(
                    showSnackbar({
                      message:
                        'Could not upload canvas pages for copy. Check network or sign in again.',
                      type: 'error',
                    }),
                  );
                  dispatch(setLoading(false));
                  return;
                }

                CANVAS_CREATE_OPTIONAL_KEYS.forEach((k) => {
                  const value = det[k];
                  if (value !== undefined && value !== null && value !== '') {
                    try {
                      simplePayload[k] =
                        typeof value === 'object'
                          ? JSON.parse(JSON.stringify(value))
                          : value;
                    } catch {
                      simplePayload[k] = value;
                    }
                  }
                });
              } else if (
                sourceNode.nodeType === 'rich-text' ||
                sourceNode.nodeType === 'rich_text' ||
                sourceNode.nodeType === 'richText'
              ) {
                // Match rich text POST /create body (see RichTextForm richtextData +
                // .git/rich_text_add_pyaload.js); version-node-copy/create uses `type: "rich-text"`.
                if (
                  !srcChild ||
                  (srcChild.type !== 'rich-text' &&
                    srcChild.type !== 'rich_text' &&
                    srcChild.type !== 'richText')
                ) {
                  dispatch(
                    showSnackbar({
                      message: 'Source rich text not found for copy.',
                      type: 'error',
                    }),
                  );
                  dispatch(setLoading(false));
                  return;
                }
                const det = srcChild.detail || {};
                simplePayload.type = 'rich-text';
                simplePayload.section_id_ref = payload.to_section_id_ref || '';
                simplePayload.step_id_ref = payload.to_step_id_ref || '';
                simplePayload.substep_id_ref = payload.to_substep_id_ref || '';
                simplePayload.short_description = det.short_description ?? '';
                simplePayload.part_required_id_ref = targetDocument.partId;
                const rtc = det.rich_text_content;
                simplePayload.rich_text_content =
                  rtc === undefined || rtc === null
                    ? ''
                    : typeof rtc === 'object'
                      ? JSON.parse(JSON.stringify(rtc))
                      : rtc;
                simplePayload.rich_text_category_ref =
                  'rich_text_category_ref' in det
                    ? det.rich_text_category_ref
                    : null;
              } else if (sourceNode.nodeType === 'video') {
                // Match video-tree-node /create body (VideoForm jsonData + createVideo spread).
                if (
                  !srcChild ||
                  String(srcChild.type || '').toLowerCase() !== 'video'
                ) {
                  dispatch(
                    showSnackbar({
                      message: 'Source video not found for copy.',
                      type: 'error',
                    }),
                  );
                  dispatch(setLoading(false));
                  return;
                }
                simplePayload.type = 'video';
                simplePayload.section_id_ref = payload.to_section_id_ref || '';
                simplePayload.step_id_ref = payload.to_step_id_ref || '';
                simplePayload.substep_id_ref = payload.to_substep_id_ref || '';
                simplePayload.revision_id_ref = targetDocument.revisionId;
                simplePayload.part_required_id_ref = targetDocument.partId;
                const detTree = findVideoDetailFromSourceTree(
                  sourceAllNodes,
                  srcChild,
                );
                const detDirect = srcChild.detail || {};
                const childSourceNodeForVideo = activeAuditTrailData
                  ? enrichChildSourceNodeFromAuditRow(
                      sourceNode,
                      activeAuditTrailData,
                      effectiveBreadcrumbText,
                    )
                  : sourceNode;
                let childDataVideo = null;
                if (sourceStep) {
                  childDataVideo = (sourceStep.children || []).find((c) =>
                    childMatchesSourceNode(c, childSourceNodeForVideo),
                  );
                } else if (sourceSubstep) {
                  childDataVideo = (sourceSubstep.children || []).find((c) =>
                    childMatchesSourceNode(c, childSourceNodeForVideo),
                  );
                }
                const detAlt = childDataVideo?.detail || {};
                const auditVc =
                  (activeAuditTrailData &&
                    (mergeAuditTrailRowVersionContentDeep(
                      activeAuditTrailData,
                    ) ||
                      mergeAuditVersionContent(activeAuditTrailData))) ||
                  {};
                const vhAuditVideoFields =
                  isVersionHistory && activeAuditTrailData
                    ? pickVideoFieldsFromAuditRowForNode(
                        activeAuditTrailData,
                        childSourceNodeForVideo,
                      )
                    : { formVideoUrl: '', playbackUrl: '' };
                const formVideoUrl =
                  (isVersionHistory && vhAuditVideoFields.formVideoUrl) ||
                  pickVideoUrlFormFieldFromDetail(detDirect) ||
                  pickVideoUrlFormFieldFromDetail(detTree) ||
                  pickVideoUrlFormFieldFromDetail(detAlt) ||
                  '';

                let playbackVideo =
                  (isVersionHistory && vhAuditVideoFields.playbackUrl) ||
                  pickPlaybackVideoUrlFromDetail(detDirect, srcChild) ||
                  pickPlaybackVideoUrlFromDetail(detTree, null) ||
                  pickPlaybackVideoUrlFromDetail(detAlt, childDataVideo) ||
                  '';

                let primary =
                  playbackVideo ||
                  pickVideoStreamUrlFromDetail(detDirect, srcChild) ||
                  pickVideoStreamUrlFromDetail(detTree, null) ||
                  pickVideoStreamUrlFromDetail(detAlt, childDataVideo) ||
                  deepFindFirstMediaUrl(detDirect) ||
                  deepFindFirstMediaUrl(detTree) ||
                  deepFindFirstMediaUrl(detAlt) ||
                  deepFindFirstMediaUrl(srcChild) ||
                  (!isVersionHistory &&
                    (pickVideoStreamUrlFromDetail(auditVc, null) ||
                      deepFindFirstMediaUrl(auditVc) ||
                      deepFindFirstMediaUrl(
                        activeAuditTrailData?.version_content || {},
                      ))) ||
                  (isVersionHistory &&
                    !vhAuditVideoFields.playbackUrl &&
                    !vhAuditVideoFields.formVideoUrl &&
                    (pickVideoStreamUrlFromDetail(auditVc, null) ||
                      deepFindFirstMediaUrl(auditVc) ||
                      deepFindFirstMediaUrl(
                        activeAuditTrailData?.version_content || {},
                      )));
                if (!playbackVideo) {
                  playbackVideo = primary;
                }
                if (!primary && sourceNode?.detail) {
                  primary =
                    pickVideoStreamUrlFromDetail(sourceNode.detail, null) ||
                    deepFindFirstMediaUrl(sourceNode.detail);
                  if (!playbackVideo) playbackVideo = primary;
                }
                if (!primary) {
                  primary =
                    deepFindFirstMediaUrl(detAlt, 0, 14) ||
                    deepFindFirstMediaUrl(detDirect, 0, 14) ||
                    deepFindFirstMediaUrl(detTree, 0, 14) ||
                    deepFindFirstMediaUrl(srcChild, 0, 14);
                  if (!playbackVideo) playbackVideo = primary;
                }
                simplePayload.short_description =
                  detDirect.short_description ??
                  detTree?.short_description ??
                  detAlt.short_description ??
                  '';
                // VideoForm: `video_url` = "Video Url" field; `video` = upload / playback stream.
                simplePayload.video_url =
                  formVideoUrl || playbackVideo || primary || '';
                simplePayload.video =
                  playbackVideo || formVideoUrl || primary || '';
              } else if (sourceNode.nodeType === 'attachment') {
                // Match attachment-tree /create body (AttachmentForm jsonData) + `type: "attachment"`.
                if (
                  !srcChild ||
                  String(srcChild.type || '').toLowerCase() !== 'attachment'
                ) {
                  dispatch(
                    showSnackbar({
                      message: 'Source attachment not found for copy.',
                      type: 'error',
                    }),
                  );
                  dispatch(setLoading(false));
                  return;
                }
                const detDirect = srcChild.detail || {};
                const childSourceNodeForAttachment = activeAuditTrailData
                  ? enrichChildSourceNodeFromAuditRow(
                      sourceNode,
                      activeAuditTrailData,
                      effectiveBreadcrumbText,
                    )
                  : sourceNode;
                let childDataAttachment = null;
                if (sourceStep) {
                  childDataAttachment = (sourceStep.children || []).find((c) =>
                    childMatchesSourceNode(c, childSourceNodeForAttachment),
                  );
                } else if (sourceSubstep) {
                  childDataAttachment = (sourceSubstep.children || []).find((c) =>
                    childMatchesSourceNode(c, childSourceNodeForAttachment),
                  );
                }
                const detAlt = childDataAttachment?.detail || {};
                simplePayload.type = 'attachment';
                simplePayload.section_id_ref = payload.to_section_id_ref || '';
                simplePayload.step_id_ref = payload.to_step_id_ref || '';
                simplePayload.substep_id_ref = payload.to_substep_id_ref || '';
                simplePayload.revision_id_ref = targetDocument.revisionId;
                simplePayload.short_description =
                  detDirect.short_description ?? detAlt.short_description ?? '';
                simplePayload.part_required_id_ref = targetDocument.partId;
                let attachmentUrls = normalizeAttachmentUrlsForVersionCopy(
                  detDirect,
                  srcChild,
                );
                if (!attachmentUrls.length) {
                  attachmentUrls = normalizeAttachmentUrlsForVersionCopy(
                    detAlt,
                    childDataAttachment,
                  );
                }
                if (!attachmentUrls.length) {
                  const one =
                    deepFindFirstMediaUrl(detDirect, 0, 14) ||
                    deepFindFirstMediaUrl(detAlt, 0, 14) ||
                    deepFindFirstMediaUrl(srcChild, 0, 14);
                  if (one) attachmentUrls = [one];
                }
                simplePayload.attachment = attachmentUrls;
              } else {
                const childSourceNodeForApi = activeAuditTrailData
                  ? enrichChildSourceNodeFromAuditRow(
                      sourceNode,
                      activeAuditTrailData,
                      effectiveBreadcrumbText,
                    )
                  : sourceNode;
                let childData = null;
                if (sourceStep) {
                  const children = sourceStep.children || [];
                  childData = children.find((c) =>
                    childMatchesSourceNode(c, childSourceNodeForApi),
                  );
                } else if (sourceSubstep) {
                  const children = sourceSubstep.children || [];
                  childData = children.find((c) =>
                    childMatchesSourceNode(c, childSourceNodeForApi),
                  );
                }

                if (childData && childData.detail) {
                  Object.keys(childData.detail).forEach((key) => {
                    if (key === 'type') return;
                    const value = childData.detail[key];
                    if (
                      value !== '' &&
                      value !== null &&
                      value !== undefined
                    ) {
                      simplePayload[key] = value;
                    }
                  });
                }
              }
            }

            // Postgres UUID columns reject ""; omit empty strings (CopyReplace parity)
            Object.keys(simplePayload).forEach((key) => {
              const keepEmptySubstepForLeaf =
                (sourceNode.nodeType === 'rich-text' ||
                  sourceNode.nodeType === 'rich_text' ||
                  sourceNode.nodeType === 'richText' ||
                  sourceNode.nodeType === 'video' ||
                  sourceNode.nodeType === 'attachment') &&
                key === 'substep_id_ref';
              const keepEmptyVideoFields =
                sourceNode.nodeType === 'video' &&
                (key === 'video' || key === 'video_url');
              if (
                simplePayload[key] === '' &&
                key !== 'short_description' &&
                key !== 'rich_text_content' &&
                !keepEmptySubstepForLeaf &&
                !keepEmptyVideoFields
              ) {
                delete simplePayload[key];
              }
            });

            if (
              isVersionHistory &&
              Array.isArray(payload.procedure_node_data) &&
              payload.procedure_node_data.length > 0 &&
              sourceNode.nodeType !== 'section' &&
              sourceNode.nodeType !== 'canvas' &&
              sourceNode.nodeType !== 'canva' &&
              sourceNode.nodeType !== 'rich-text' &&
              sourceNode.nodeType !== 'rich_text' &&
              sourceNode.nodeType !== 'richText' &&
              sourceNode.nodeType !== 'video' &&
              sourceNode.nodeType !== 'attachment'
            ) {
              simplePayload.procedure_node_data = payload.procedure_node_data;
            }

            if (
              sourceNode.nodeType === 'rich-text' ||
              sourceNode.nodeType === 'rich_text' ||
              sourceNode.nodeType === 'richText'
            ) {
              simplePayload.type = 'rich-text';
            }
            if (sourceNode.nodeType === 'video') {
              simplePayload.type = 'video';
            }
            if (sourceNode.nodeType === 'attachment') {
              simplePayload.type = 'attachment';
            }

            simplePayload.type = normalizeVersionNodeCopyCreateType(simplePayload.type);

            copyResponse = await dispatch(copyNodeVersionHistory(simplePayload)).unwrap();
          } else {
            // Must match CopyReplace: version/version-copy-procedure-tree-node (not node-cmd/copy-pro-tree-node)
            const treePayload = omitVersionHistoryTreeCopyFromFields(payload);
            copyResponse = await dispatch(copyVersionProcedureTreeNode(treePayload)).unwrap();
          }

          // RevisionSubmission reads sections from get-all; refresh so the new section appears.
          try {
            await dispatch(
              getAllSectionsForPart({
                partId: targetDocument.partId,
                revisionId: targetDocument.revisionId,
              }),
            ).unwrap();
          } catch (refreshErr) {
            console.warn('getAllSectionsForPart after copy:', refreshErr);
          }

          setModalOpen(false);
          handleClose();

          const getNodeTypeDisplayName = (nodeType) => {
            const typeMap = {
              attachment: 'Attachment',
              video: 'Video',
              'rich-text': 'Rich Text',
              form: 'Form',
              canva: 'Canvas',
              canvas: 'Canvas',
            };
            return typeMap[nodeType] || 'Element';
          };

          const successMessage =
            sourceNode.nodeType === 'step'
              ? 'Step copied successfully!'
              : sourceNode.nodeType === 'substep'
                ? 'Substep copied successfully!'
                : ['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(
                      sourceNode.nodeType,
                    )
                  ? `${getNodeTypeDisplayName(sourceNode.nodeType)} copied successfully!`
                  : 'Section copied successfully!';
          dispatch(showSnackbar({ message: successMessage, type: 'success' }));

          const description = targetDocument.description || 'no-description';
          const encodedDescription = encodeURIComponent(description);

          const redirected = await handleRedirectToCopiedNode(
            copyResponse,
            targetDocument,
            dispatch,
            navigate,
            getRevAllNodes,
          );

          if (!redirected) {
            navigate(
              `/revision-submission/${targetDocument.partId}/${targetDocument.revision}/${targetDocument.type}/${targetDocument.partNumber}/${targetDocument.revisionId}/${encodedDescription}`,
              { state: { status: 'Not Submitted' } },
            );
          }
          window.scrollTo(0, 0);
        } catch (error) {
          console.error('Error copying node:', error);
          const errorMessage = sourceNode.nodeType === 'step' 
            ? 'Error copying step: ' + (error?.message || 'Unknown error')
            : sourceNode.nodeType === 'substep'
            ? 'Error copying substep: ' + (error?.message || 'Unknown error')
            : 'Error copying section: ' + (error?.message || 'Unknown error');
          dispatch(showSnackbar({ message: errorMessage, type: 'error' }));
        } finally {
          dispatch(setLoading(false));
        }
      }

      // Document copy flow (existing implementation)
      if (copyMode !== 'document') {
        // Safety guard: do not run document copy when not in document mode
        dispatch(setLoading(false));
        return;
      }
      // Use already fetched data if available, otherwise fetch
      const sourceResponse = newData || await dispatch(getRevAllNodes({ 
        partId: sourceDocument.partId, 
        revisionId: sourceDocument.revisionId 
      })).unwrap();

      const targetResponse = originalData || await dispatch(getRevAllNodes({ 
        partId: targetDocument.partId, 
        revisionId: targetDocument.revisionId 
      })).unwrap();

      // Get audit trail data
      const audit_trail_data = await getAuditTrailData({
        action: "REVISION_COPIED",
        comment: comment,
        version_content: {
          action_to_show: `User copied and replaced ${sourceDocument.type} ${sourceDocument.partNumber}, Rev ${sourceDocument.revision} into ${targetDocument.type} ${targetDocument.partNumber}, Rev ${targetDocument.revision}`,
          originingalContent: extractVersionStructure(targetResponse),
          newContent: extractVersionStructure(sourceResponse),
          rev: targetDocument.revision,
          status: targetDocument.status || 'Not Submitted',
          item_code_type: targetDocument.type,
          partNumber: targetDocument.partNumber,
          from_part_ref: sourceDocument.partNumber,
          from_part_rev: sourceDocument.revision,
          to_part_ref: targetDocument.partNumber,
          to_part_rev: targetDocument.revision,
        },
      },false);

      // Build payload for copy operation
      const nodeIndexingResult = buildCopyReplaceNodeIndexing({
        sourceNodes: sourceResponse,
        targetNodes: targetResponse,
        sourceDocument,
        targetDocument,
        startswith:1,
        actionToShow: audit_trail_data.version_content.action_to_show+"."
      });
      
      const payload = {
        part_id_ref: sourceDocument.partId,
        procedure_rev_ref: sourceDocument.revisionId,
        new_part_id: targetDocument.partId,
        new_procedure_id: targetDocument.revisionId,
        tree_node_id:targetDocument.tree_node_id,
        based_on_version_ref: sourceDocument.version_id_ref,
        parent_based_version: targetDocument.parent_based_version,
        audit_trail_data,
        node_indexing: {
          first_entry:{
            title:audit_trail_data.version_content.action_to_show+`. System Performs Operation 1 of ${Object.keys(nodeIndexingResult).length+1}, Copied and replaced ${sourceDocument.type} ${sourceDocument.partNumber} Rev ${sourceDocument.revision}.`,
            originalContent:audit_trail_data.version_content.originingalContent,
            newContent:[],
          },
          ...nodeIndexingResult,
        },
      };

      if(sourceResponse.length > 0){
        payload.node_indexing[sourceResponse[0]?.tree_node_id].originalContent = []
      }
      // Execute copy operation

      const result = await dispatch(copyProcedure(payload)).unwrap();
      
      // Close modal and copy replace flow
      setModalOpen(false);
      handleClose();
      
      // Show success message using snackbar
      dispatch(showSnackbar({ message: 'Copy & Replace operation completed successfully!', type: 'success' }));
      
      // Navigate to revision-submission page for target document
      const description = targetDocument.description || 'no-description';
      const encodedDescription = encodeURIComponent(description);
      navigate(
        `/revision-submission/${targetDocument.partId}/${targetDocument.revision}/${targetDocument.type}/${targetDocument.partNumber}/${targetDocument.revisionId}/${encodedDescription}`,
        { state: { status: 'Not Submitted' } }
      );
      // Scroll to top when navigating to revision-submission page
      window.scrollTo(0, 0);
      
    } catch (error) {
      console.error('Error copying procedure:', error);
      dispatch(showSnackbar({ message: 'Error copying Document: ' + (error?.message || 'Unknown error'), type: 'error' }));
      dispatch(setLoading(false));
    }
  };

  // Full-screen loader only while choosing target revision — not on paste position (or Redux isLoading
  // would hide PastePositionSelector and look like an infinite load after clicking the arrow).
  if (isLoading && step === 'select') {
    return <Loader text="Loading document data..." />;
  }

  // Get step title and buttons based on current step
  const getStepConfig = () => {
    // Generate title based on copy mode
    const getSelectTitle = () => {
      const srcPart = sourceDocument?.partNumber ?? ''
      const srcRev = sourceDocument?.revision ?? ''
      const fromProcedure = [srcPart, srcRev].filter(Boolean).join(' ') || '—'
      if (copyMode === 'node' && sourceNode) {
        // Node copy: "Copying Step 1 from {partNumber} {revision}. Select the Procedure" (matches /node document flow)
        return `Copying ${sourceNode.nodeTitle} from ${fromProcedure}. Select the Procedure`;
      } else {
        // Document copy: "Copy & Replacing {type} from {partNumber} {revision}. Select the Document"
        return `Copy & Replacing ${sourceDocument?.type ?? ''} ${srcPart}, Rev ${srcRev}. Select the Document`;
      }
    };

    switch (step) {
      case 'select':
        return {
          title: getSelectTitle(),
          subheading: '',
          buttonsBeforeTitle: [
            { label: 'Cancel', onClick: handleClose }
          ],
          buttons: []
        };
      case 'pastePosition':
        // Title and buttons handled by PastePositionSelector component
        return {
          title: `Copying ${sourceNode?.nodeTitle || ''} from ${[sourceDocument?.partNumber, sourceDocument?.revision].filter(Boolean).join(' ') || '—'}. Where you want to paste?`,
          subheading: '',
          buttonsBeforeTitle: [
            { label: 'Cancel', onClick: handleClose }
          ],
          buttons: [] // Buttons will be updated by PastePositionSelector
        };
      case 'preview':
        return {
          title: 'Please Confirm the Changes',
          subheading: '',
          showCancelButton: true,
          onCancel: handleClose,
          showViewToggle: true,
          selectedView: previewMode,
          onViewChange: (mode) => handlePreviewModeChange(mode),
          buttonsBeforeTitle: [],
          buttons: [
            { label: 'Confirm', onClick: () => setModalOpen(true) }
          ]
        };
      case 'confirm':
        return {
          title: 'Copy & Replace - Confirm',
          subheading: `Confirm copy operation for ${targetDocument.partNumber} Rev ${targetDocument.revision}`,
          buttonsBeforeTitle: [
            { label: 'Back', onClick: handleBack },
            { label: 'Cancel', onClick: handleClose }
          ],
          buttons: []
        };
      default:
        return {
          title: 'Copy & Replace',
          subheading: '',
          buttonsBeforeTitle: [],
          buttons: []
        };
    }
  };

  const stepConfig = getStepConfig();

  return (
    <div>
      {/* Only render header if not in pastePosition step (handled by PastePositionSelector) */}
      {step !== 'pastePosition' && (
        <VersionHistoryCopyAppBar 
          title={stepConfig.title}
          subheading={stepConfig.subheading}
          buttons={stepConfig.buttons}
          buttonsBeforeTitle={stepConfig.buttonsBeforeTitle || []}
          showBackButton={false}
          showProfileButton={false}
          showTablesDropdown={false}
          showCancelButton={stepConfig.showCancelButton || false}
          onCancel={stepConfig.onCancel}
          showViewToggle={stepConfig.showViewToggle || false}
          selectedView={stepConfig.selectedView}
          onViewChange={stepConfig.onViewChange}
        />
      )}
      
      {/* Step 1: Select Target Document */}
      {step === 'select' && (
        <div style={{ padding: '20px' }}>
          
          <SelectRevisionTable
            sourcePartNumber={sourceDocument?.partNumber ?? ''}
            onRevisionSelect={handleRevisionSelect}
            fromRevision={sourceDocument?.revision ?? ''}
            procedureRevisionId={sourceDocument?.revisionId}
          />
        </div>
      )}

      {/* Step 2: Select Paste Position (only for node copy) */}
      {step === 'pastePosition' && targetDocument && copyMode === 'node' && (
        <PastePositionSelector
          onPositionSelected={handlePositionSelected}
          onCancel={handleClose}
        />
      )}
      
      {/* Step 3: Preview */}
      {step === 'preview' && targetDocument && (
        <CopyReplacePreview
          sourceDocument={sourceDocument}
          sourceNode={sourceNode}
          copyMode={copyMode}
          targetDocument={targetDocument}
          pastePosition={pastePosition}
          previewMode={previewMode}
          originalData={originalData}
          newData={newData}
          onPreviewModeChange={handlePreviewModeChange}
          onConfirm={handleConfirmSave}
          comment={comment}
          onCommentChange={handleCommentChange}
          isLoading={isLoading}
          isModalOpen={isModalOpen}
          onModalClose={() => setModalOpen(false)}
          breadcrumbText={breadcrumbText}
        />
      )}
      
      {/* Step 3: Confirm */}
      {step === 'confirm' && (
        <CopyReplaceConfirm
          sourceDocument={sourceDocument}
          targetDocument={targetDocument}
          comment={comment}
          onCommentChange={handleCommentChange}
          onConfirm={() => {
            // TODO: Implement actual copy operation
            handleClose();
          }}
        />
      )}
    </div>
  );
};

export default CopyReplace;
